/**
 * AxiomOrderModal.tsx
 *
 * Thin LabOrderModalCore adapter for Axiom peptide / Rx orders. Staff selects
 * catalog items with quantities, enters prescriber info and directions, then
 * reviews patient demographics (auto-populated from the patient record). Any
 * edits to demographics, address, or gender are saved back to the patient.
 *
 * The order is always created on submit. When the practice requires payment up
 * front (integration.paymentEnforced) the server parks it in 'awaiting_payment'
 * and withholds it from the pharmacy until the fee is collected — the parent
 * then opens the payment modal against the created order.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Minus, Plus, Search, Syringe } from 'lucide-react';
import {
  createIntegrationOrder,
  updateIntegrationOrder,
  getIntegrationOrderDefaults,
  getIntegrationPrescribers,
  getAxiomCatalog,
  getAxiomBillingStatus,
  getAxiomNotices,
  lookupNpi,
  saveIntegrationProviderNpi,
  type EnabledIntegration,
  type CreateIntegrationOrderResult,
  type OrderItemSelection,
  type IntegrationOrder,
  type IntegrationPrescriber,
  type IntegrationProduct,
  type NpiLookupResult,
  type AxiomNotice,
} from '@/services/integrations.service';
import { useAuthStore } from '@/store/authStore';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { oneLineSig } from './orderFormParts';
import { Hint, SectionTitle } from './orderFormParts';
import { LabOrderModalCore, type LabOrderConfig, type LabOrderSubmitResult, type InitialShipTo, type ShipToChoice } from './LabOrderModalCore';
import { shippingSlaText } from './shippingSla';
import { listFacilities } from '@/services/admin.service';
import { getPatient, updatePatient } from '@/services/patients.service';
import {
  addOnType,
  categoryOf,
  isAddOnProduct,
  isVialProduct,
  isPenProduct,
  isPenBundleProduct,
  isShippingProduct,
  defaultShippingProduct,
  SHIPPING_LABEL,
  findPenAddOn,
  findPenBundleAddOn,
  needleAddOns,
  premiumNeedleUpgrade,
  AXIOM_VIALS_KEY,
} from './integrationAddOns';
import type { ChargePaymentPrefillItem } from '@/pages/payments/ChargePaymentModal';
import { toStateCode } from '@/lib/usStates';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';

/**
 * One order created by a submit. A combined (pens + vials) cart creates up to
 * two — one per Axiom module — each with the charge lines that order bills.
 */
export interface AxiomCreatedOrder {
  /** Integration key the order was placed against ('axiom' | 'axiom_vials'). */
  key: string;
  result: CreateIntegrationOrderResult;
  prefillItems: ChargePaymentPrefillItem[];
}

/** A cart id shared by the orders a combined checkout splits into. */
const newCartGroupId = (): string => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return typeof c?.randomUUID === 'function'
    ? c.randomUUID()
    : `cart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

type CombinedSection = 'pens' | 'vials' | 'supplies';

interface AxiomOrderModalProps {
  patientId: number;
  integration: EnabledIntegration;
  /** Axiom-family integration key the order flow targets ('axiom' pens —
   *  default — or 'axiom_vials'). Sent as the order `key` and used for the
   *  catalog / NPI save-back endpoints. */
  integrationKey?: string;
  isOpen: boolean;
  /** When set, the form opens prefilled from this order (Edit). */
  editOrder?: IntegrationOrder | null;
  /** When set, the form opens prefilled from this order but submits as a NEW
   *  order (Re-order) — same prescription, fresh order + payment. */
  seedOrder?: IntegrationOrder | null;
  /**
   * Reuse THIS order's shipping address on a new order (Clone).
   *
   * Separate from `seedOrder` on purpose: a plain re-order takes the
   * prescription but lets the ship-to come from the patient's own addresses,
   * because a months-old delivery address must not ride silently into a new
   * order. A clone says "this order, again" and carries it deliberately.
   */
  shipFromOrder?: IntegrationOrder | null;
  /** Set when this new order re-sends a cancelled one; recorded in its activity log. */
  resentFromOrderId?: number | null;
  onClose: () => void;
  onOrdered: (result: CreateIntegrationOrderResult, prefillItems: ChargePaymentPrefillItem[]) => void;
  /**
   * Combined Axiom checkout: the vials module's integration. When set (and
   * the form is a NEW order — never an edit, re-order or clone), the modal
   * offers ONE catalog in three sections (Peptide Pens / Vials / Supplies) and
   * a single cart, then splits it on submit into a pens order (`integration`)
   * and a vials order (this one). Each order keeps its own module settings and
   * shipping; both carry the same `metadata.cartGroupId` so one payment can
   * release both.
   */
  vialsIntegration?: EnabledIntegration | null;
  /**
   * Combined mode: every order the submit created, pens first. Falls back to
   * calling `onOrdered` once per order when not provided.
   */
  onOrderedGroup?: (orders: AxiomCreatedOrder[]) => void;
  /** Called after an in-place edit (PATCH) succeeds — parent refreshes the list. */
  onUpdated?: () => void;
  /**
   * What a patient already paid for, shown as a read-only reference while the
   * order is built (the Fulfill flow from App Orders).
   *
   * REFERENCE ONLY, never a preselection. A practice's catalog item and an
   * Axiom product share nothing but a name, and the names disagree in exactly
   * the way that matters — "CJC 1295/Ipamorelin (1000)" against
   * "CJC-1295 / Ipamorelin 5mg". Matching on that would put a dose the
   * prescriber did not choose into a prescription. Staff read this and pick.
   */
  paidReference?: PaidReference | null;
}

export interface PaidReference {
  paymentId: number;
  paidOn: string | null;
  patientName: string | null;
  items: Array<{ name: string | null; quantity: number; amount: number }>;
}

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/**
 * Fixed contractual section order for categorized catalogs (the Axiom Vials
 * Exhibit B sheet). Unknown categories sort after these; catalogs whose
 * products carry no metadata.category (pens) keep the flat list.
 */
/**
 * Licensure credentials the prescriber dropdown offers.
 *
 * ONE list, because it is read in four places — the dropdown, both prefill
 * paths, and the submit-time error message — and when they were four literals
 * a credential added to the dropdown silently failed to prefill from the
 * physician record, which reads as "it forgot my prescriber".
 *
 * The API does not enforce an allowlist (AxiomIntegration strips punctuation
 * and forwards whatever it is given), so this is the only gate; adding one
 * here is all that is needed to let a practice order under it.
 */
const PRESCRIBER_CREDENTIALS = ['MD', 'DO', 'NP', 'PA', 'ND'] as const;

const CATEGORY_ORDER = ['PEPTIDE VIALS', 'GLP-1 VIALS', 'NASAL SPRAYS', 'SUPPLIES', 'ANTI-EMETIC'];

// `categoryOf` / `isShippingProduct` live in integrationAddOns.ts so the tests
// can import the same rules. SHIPPING is NOT a section and nobody picks it
// (SCRUM-2023): every vials order carries the one general shipping fee
// (defaultShippingProduct), added here, quantity 1 — the server still guards
// SHIPPING_REQUIRED / SHIPPING_DUPLICATE. The pharmacy decides cold vs not.

/**
 * Consumables — syringes, alcohol pads. Not something the patient TAKES, so
 * they carry no dosing and must be exempt from the directions requirement, the
 * same way shipping and devices are. Without this, defaulting the syringes onto
 * a vial order would block every one of those orders until someone typed
 * instructions for a box of needles.
 */
const isSupplyProduct = (p: IntegrationProduct): boolean => categoryOf(p) === 'SUPPLIES';

/**
 * The syringes a vial is useless without.
 *
 * Other vendors put them in the box, so providers do not think to add them and
 * the patient receives a vial they cannot draw from (SUP-000799). Matched on
 * the 1cc kit's sku, with a name fallback so a re-keyed catalog row does not
 * silently stop defaulting.
 */
const DEFAULT_SYRINGE_SKU = 'AXM-SUP-SYR-1CC-10CT';
const DEFAULT_SYRINGE_QTY = 3;
const isDefaultSyringe = (p: IntegrationProduct): boolean =>
  (p.key ?? '') === DEFAULT_SYRINGE_SKU
  || (categoryOf(p) === 'SUPPLIES' && /insulin/i.test(p.name) && /\b1\s*cc\b/i.test(p.name));

/**
 * The catalog's suggested directions (sig) for an item.
 *
 * Two keys carry the same content: `sig` is the legacy one (the pens catalog
 * uses it), while the vials catalog — and the Corp super-admin items table's
 * editable "Dosing instructions" column, which is what staff maintain going
 * forward — stores it as `dosingInstructions`. Every prod vials item has only
 * `dosingInstructions`, so reading `sig` alone left all of them with no
 * suggested sig at all. First non-empty wins; both keys keep working.
 *
 * Single source of truth: the item row, the confirm summary, and the submitted
 * itemDirections all resolve through here so they can never disagree.
 */
const suggestedDirections = (p: Pick<IntegrationProduct, 'metadata'>): string => {
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  return str(p.metadata?.sig) || str(p.metadata?.dosingInstructions);
};

/**
 * Protocol spec lines for the blue verification box — one line per spec column
 * of Axiom's product sheet, in sheet order.
 *
 * The two catalogs spell the same facts differently: the pens seeder wrote
 * `strength` / `injectionsPerCart` / `weeksPerCart` / `budDays`, while the
 * vials sheet import writes `concentration` / `dosesPerVial` / `daysSupplied` /
 * `bud`. Reading only the pens spellings is why a vials item rendered just
 * three spec lines. Each entry therefore lists its candidate keys in priority
 * order — first non-empty wins — and a candidate may override the label (a
 * pen's doses are per cartridge, a vial's are per vial) or format the value.
 *
 * Deliberately absent: Clinic List Price and Margin. This box is the clinical
 * verification panel shown before ordering for a patient; the price staff pay
 * is already on the row, and gross margin is not a prescribing fact.
 */
type SpecSource = { key: string; label?: string; format?: (v: unknown) => string };

const SPEC_LINES: { label: string; from: SpecSource[] }[] = [
  { label: 'SKU', from: [{ key: 'sku' }] },
  { label: 'Concentration', from: [{ key: 'concentration' }, { key: 'strength' }] },
  { label: 'Fill / size', from: [{ key: 'fillSize' }] },
  { label: 'Total mg', from: [{ key: 'totalMg' }] },
  { label: 'Recommended dose', from: [{ key: 'recommendedDose' }] },
  { label: 'Frequency', from: [{ key: 'frequency' }] },
  {
    label: 'Doses / vial',
    from: [{ key: 'dosesPerVial' }, { key: 'injectionsPerCart', label: 'Injections / cartridge' }],
  },
  {
    label: 'Supply duration',
    // The vials sheet's SUPPLY DURATION is already a phrase ("4 weeks");
    // the pens catalog stores a bare week count.
    from: [{ key: 'daysSupplied' }, { key: 'weeksPerCart', format: (v) => `${v} weeks` }],
  },
  {
    label: 'Beyond-use date',
    // `bud` arrives from the sheet as a bare day count; don't double the unit
    // if a future row ever spells it out.
    from: [
      { key: 'bud', format: (v) => (/^\d+$/.test(String(v).trim()) ? `${v} days` : String(v)) },
      { key: 'budDays', format: (v) => `${v} days` },
    ],
  },
];

/** Resolve SPEC_LINES against one item's metadata, dropping anything unset. */
const specLines = (meta: IntegrationProduct['metadata']): { label: string; value: string }[] => {
  const out: { label: string; value: string }[] = [];
  for (const line of SPEC_LINES) {
    for (const src of line.from) {
      const raw = meta?.[src.key];
      if (raw == null || String(raw).trim() === '') continue;
      const value = (src.format ? src.format(raw) : String(raw)).trim();
      if (value !== '') out.push({ label: src.label ?? line.label, value });
      break;
    }
  }
  return out;
};

/**
 * The practice's marketing name for an item, when it has one. Only the eight
 * named Axiom pens do. Always rendered next to the catalog name rather than in
 * place of it: staff ordering for a patient need to see the peptide, and a
 * historical order's stored line item keeps whichever name was current then.
 */
const brandNameOf = (p: Pick<IntegrationProduct, 'metadata'>): string =>
  typeof p.metadata?.brandName === 'string' ? p.metadata.brandName.trim() : '';

/**
 * Every identifier a live catalog answers to (sku + aliases, lower-cased). See
 * `catalogIds` in the modal for why an empty set means "unknown", not "none".
 */
const catalogIdSet = (catalog: Array<{ sku?: string | null; aliases?: unknown }> | undefined): Set<string> => {
  const ids = new Set<string>();
  for (const c of catalog ?? []) {
    for (const id of [c.sku, ...((c.aliases ?? []) as string[])]) {
      const key = (id ?? '').trim().toLowerCase();
      if (key) ids.add(key);
    }
  }
  return ids;
};


/**
 * QtyStepper — [−] value [+] quantity control (the UX Movement "number picker"
 * pattern): large clickable step buttons instead of the native micro-spinner
 * arrows, which invite misclicks. The middle value stays typeable for staff
 * who prefer it (native spinner suppressed; typed values clamp to `min`,
 * exactly like the number inputs this replaced). The − button disables at
 * `min` so the value can't go below it.
 */
function QtyStepper({
  value, min, onChange, disabled = false, label, title,
}: {
  value: number;
  min: number;
  onChange: (next: number) => void;
  /** Disables the whole control — both buttons and the value (backordered items). */
  disabled?: boolean;
  /** Accessible name for the value; buttons become "Decrease/Increase <label>". */
  label: string;
  title?: string;
}) {
  const clamp = (n: number) => Math.max(min, Number.isFinite(n) ? n : min);
  const btn =
    'flex h-9 w-9 shrink-0 items-center justify-center text-slate-600 transition-colors ' +
    'enabled:hover:bg-slate-100 enabled:hover:text-slate-900 ' +
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-500 ' +
    'disabled:cursor-not-allowed disabled:text-slate-300';
  return (
    <div
      title={title}
      className={`flex shrink-0 items-stretch overflow-hidden rounded-md border border-slate-300 bg-white ${disabled ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={disabled || value <= min}
        className={btn}
        onClick={() => onChange(clamp(value - 1))}
      >
        <Minus className="h-4 w-4" />
      </button>
      <input
        type="number"
        min={min}
        inputMode="numeric"
        aria-label={label}
        disabled={disabled}
        value={String(value)}
        onChange={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
        className="w-10 border-x border-slate-300 bg-transparent text-center text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button
        type="button"
        aria-label={`Increase ${label}`}
        disabled={disabled}
        className={btn}
        onClick={() => onChange(clamp(value + 1))}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Field label with an optional required marker and an explanatory tooltip. */
function FieldLabel({ children, required, hint }: { children: ReactNode; required?: boolean; hint: string }) {
  return (
    <label className="mb-0.5 flex items-center gap-1 text-xs font-medium text-slate-600">
      <span>{children}{required && <span className="text-red-500"> *</span>}</span>
      <Hint text={hint} />
    </label>
  );
}

/** Section header with a tooltip + optional required marker. */

export function AxiomOrderModal({ patientId, integration, integrationKey = 'axiom', isOpen, editOrder, seedOrder, shipFromOrder, resentFromOrderId, onClose, onOrdered, onUpdated, paidReference, vialsIntegration, onOrderedGroup }: AxiomOrderModalProps) {
  const resentFromRef = useRef(resentFromOrderId ?? null); resentFromRef.current = resentFromOrderId ?? null;
  // A plain Re-order (seeded from an order, not a clone, re-send or edit)
  // records the order it repeats, so that order stops offering Re-order even
  // if this one changes its items (see computeSupersededBy).
  const reorderOfId = !editOrder && seedOrder && !shipFromOrder && !resentFromOrderId ? seedOrder.integrationOrderId : null;
  const reorderOfRef = useRef(reorderOfId); reorderOfRef.current = reorderOfId;
  // Prefill from either an edit target or a re-order seed; only editOrder makes
  // submit a PATCH (see editOrderRef), so a seed submits as a fresh order.
  const prefillSource = editOrder ?? seedOrder ?? null;
  // Re-order (seed) vs Edit — the two prefill paths differ on the pen: a
  // re-order is cartridge-only because the patient already owns the durable pen
  // from the order being repeated, while an edit must faithfully show what the
  // existing order contains (dropping it would silently strip it on save).
  const isReorderSeed = !editOrder && !!seedOrder;
  // Combined pens + vials cart — new orders only. Anything that starts from an
  // existing order (edit / re-order / clone / re-send) is about THAT order's
  // module and stays single-integration.
  const combined = !!vialsIntegration && !editOrder && !seedOrder && !shipFromOrder;
  /** The primary integration's products (pens in combined mode). */
  const pensProducts = integration.products ?? [];
  const vialsSourceProducts = combined ? (vialsIntegration?.products ?? []) : [];
  // Product ids are unique across modules, so one qty map serves both; this set
  // is what tags each product with the module (order) it belongs to.
  const vialsIds = useMemo(
    () => new Set(vialsSourceProducts.map((p) => p.integrationProductId)),
    [vialsSourceProducts],
  );
  const isVialsSourced = (p: { integrationProductId: number }) => vialsIds.has(p.integrationProductId);
  const products = useMemo(
    () => (vialsSourceProducts.length > 0 ? [...pensProducts, ...vialsSourceProducts] : pensProducts),
    [pensProducts, vialsSourceProducts],
  );

  // Live Axiom catalog → product images, stock, and per-product state
  // licensing for the item list. Short staleTime: `unavailableUntil` and
  // `availableStates` both gate ordering, so keep them fresh. Keyed per
  // integration so pens/vials never share a cache entry. Declared before
  // orderableProducts (below) so the stock helpers exist before the first
  // list built from the catalog.
  const { data: primaryCatalogData } = useQuery({
    queryKey: ['axiom-catalog', integrationKey],
    queryFn: () => getAxiomCatalog(integrationKey),
    enabled: isOpen,
    staleTime: 60_000,
  });
  // Combined cart: the vials module's catalog too, so stock and per-state
  // licensing gate the vials items exactly as they do in the vials-only form.
  const { data: vialsCatalogData } = useQuery({
    queryKey: ['axiom-catalog', AXIOM_VIALS_KEY],
    queryFn: () => getAxiomCatalog(AXIOM_VIALS_KEY),
    enabled: isOpen && combined,
    staleTime: 60_000,
  });
  const catalogData = useMemo(
    () => (combined
      ? { catalog: [...(primaryCatalogData?.catalog ?? []), ...(vialsCatalogData?.catalog ?? [])] }
      : primaryCatalogData),
    [combined, primaryCatalogData, vialsCatalogData],
  );
  /**
   * Per-sku `availableStates` from Axiom's live catalog — PER-PRODUCT state
   * licensing, separate from whether Axiom's pharmacy network covers a state
   * at all. Absent from the map = Axiom hasn't told us anything for this item
   * (unrestricted, fail open — we do not block on data we do not have). A key
   * present with an EMPTY array means Axiom ships it nowhere right now; see
   * backorderedUntil(). Same alias-bridging idiom as unavailableBySku
   * below — mirrors AxiomCatalogIndex::statesFor() on the server.
   */
  const statesBySku = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const c of catalogData?.catalog ?? []) {
      if (!Array.isArray(c.availableStates)) continue;
      const states = c.availableStates.map((s) => s.trim().toUpperCase());
      for (const id of [c.sku, ...((c.aliases ?? []) as string[])]) {
        const key = (id ?? '').trim().toLowerCase();
        if (key && !(key in m)) m[key] = states;
      }
    }
    return m;
  }, [catalogData]);
  const availableStatesFor = (p: { metadata?: { sku?: string } | null; key: string }): string[] | null =>
    statesBySku[(p.metadata?.sku ?? p.key ?? '').trim().toLowerCase()] ?? null;
  /**
   * Identifier → back-order info for anything Axiom says is unavailable:
   * back-ordered, dated unavailable, or turned off (ships to no state).
   *
   * TWO STOCK FIELDS, AND THEY DO NOT MOVE TOGETHER. `backordered` is a flag
   * with an `expectedBackAt` date; `unavailableUntil` is a date on its own.
   * Reading only the latter — which this used to do — reports everything as in
   * stock while Retatrutide, PT-141 and others are back-ordered, because Axiom
   * currently leaves `unavailableUntil` null on every entry. Reading only the
   * flag would break the day that flips. The union survives either.
   *
   * KEYED BY CANONICAL SKU **AND** BY EVERY ALIAS. Axiom runs two SKU
   * conventions at once (short codes and slugs) and about half the entries
   * carry an `aliases` list bridging them — the slug entry for BPC-157 lists
   * "AXM-VL-BPC", which is what we store. Matching the canonical sku alone
   * silently misses those, and a missed match shows a back-ordered item as
   * orderable. Lower-cased on both sides because the conventions differ in case.
   *
   * Mirrors AxiomCatalogIndex on the server, which the fulfil gate and the
   * catalog-health report share — the three used to disagree.
   */
  const unavailableBySku = useMemo(() => {
    const m: Record<string, string | true> = {};
    for (const c of catalogData?.catalog ?? []) {
      const dated = c.unavailableUntil && new Date(c.unavailableUntil).getTime() > Date.now();
      // An explicit EMPTY availableStates is how Axiom turns a product off:
      // it ships nowhere, so it is out of stock with no date to show.
      const turnedOff = Array.isArray(c.availableStates) && c.availableStates.length === 0;
      if (!c.backordered && !dated && !turnedOff) continue;
      // `true` when it is out of stock with no date to show.
      const until: string | true = (c.backordered || dated)
        ? c.expectedBackAt || (dated ? c.unavailableUntil! : true)
        : true;
      for (const id of [c.sku, ...((c.aliases ?? []) as string[])]) {
        const key = (id ?? '').trim().toLowerCase();
        if (key && !(key in m)) m[key] = until;
      }
    }
    return m;
  }, [catalogData]);
  /**
   * Every identifier the live catalog answers to. A product of ours that is
   * in none of them has been taken off Axiom's catalog — the fulfil gate
   * rejects it as `unknown_product`, so offering it here only sets up a failed
   * order after the patient pays. Only once a catalog actually loaded: an
   * empty list is a fetch failure, not "nothing is orderable".
   */
  // Per module: a vials catalog that failed to load must not mark every vials
  // item "no longer listed" just because the pens catalog did load.
  const catalogIds = useMemo(() => catalogIdSet(primaryCatalogData?.catalog), [primaryCatalogData]);
  const vialsCatalogIds = useMemo(() => catalogIdSet(vialsCatalogData?.catalog), [vialsCatalogData]);
  /**
   * Out of stock per Axiom's live catalog: back-ordered, turned off (ships to
   * no state), or no longer listed. A date when Axiom gave one, `true` when it
   * did not, null when the item is orderable. Shipping lines are fees, never
   * in the catalog, so they are never out of stock.
   */
  const backorderedUntil = (p: IntegrationProduct): string | true | null => {
    const key = (p.metadata?.sku ?? p.key ?? '').trim().toLowerCase();
    if (key in unavailableBySku) return unavailableBySku[key];
    const ids = isVialsSourced(p) ? vialsCatalogIds : catalogIds;
    if (ids.size > 0 && !isShippingProduct(p) && !ids.has(key)) return true;
    return null;
  };
  const fmtBackorderDate = (iso: string | true) =>
    iso === true ? '' : new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  /** Badge text: a date when Axiom gave one, plain "Out of stock" otherwise. */
  const stockLabel = (until: string | true) =>
    until === true ? 'Out of stock' : `Back-ordered until ${fmtBackorderDate(until)}`;

  // Items (non add-on) shown in the picker, sorted A→Z by name. Out-of-stock
  // ones stay listed — struck through and disabled — so staff can see Axiom
  // has them off rather than wonder where they went.
  const orderableProducts = products
    .filter((p) => !isAddOnProduct(p))
    .sort((a, b) => a.name.localeCompare(b.name));
  // The primary integration's items. Identical to orderableProducts outside
  // combined mode; in combined mode the pens half only, so the pen modes below
  // are decided by the pens catalog alone — the merged list would read as
  // "categorized" (vials carry categories) and switch the pen split off.
  const pensOrderable = combined ? orderableProducts.filter((p) => !isVialsSourced(p)) : orderableProducts;
  // Combined mode's Vials section (every vials category but SUPPLIES, which
  // live under Supplies, and SHIPPING, which has its own box) and the vials
  // half of the Supplies section.
  const vialsPickable = combined
    ? orderableProducts.filter((p) => isVialsSourced(p) && !isSupplyProduct(p) && !isShippingProduct(p))
    : [];
  const vialsSupplies = combined ? orderableProducts.filter((p) => isVialsSourced(p) && isSupplyProduct(p)) : [];
  // Category-grouped picker (vials): products carry metadata.category. Pens
  // products don't, and keep today's flat list untouched.
  const categorized = pensOrderable.some((p) => categoryOf(p) !== '');

  const shippingProducts = orderableProducts.filter(isShippingProduct);
  // The one shipping fee every vials order carries — never a choice.
  const defaultShipping = defaultShippingProduct(shippingProducts);
  // ── Pen + Cartridge vs Cartridge only (pens module) ───────────────────────
  // A peptide cartridge can't be self-administered without an injector pen, so
  // the same catalog is offered as two lists: a first order that needs the
  // device (cartridge + pen, one pen per cartridge) and a refill that doesn't
  // (cartridge alone, at the catalog price).
  //
  // The premium is NOT a client-side markup — the server prices every line from
  // the catalog, so a UI-only "+$50" would display one number and bill another.
  // Instead the bundle is its own catalog row whose price IS the premium, added
  // as a real line item. That also satisfies the pharmacy, which needs both the
  // cartridge and the pen SKU on the order to know what to ship.
  const penBundle = findPenBundleAddOn(pensProducts);
  // Both lists render the same peptides, so the split only makes sense on a
  // flat (pens) catalog that actually has the bundle row configured.
  const penChoice = !!penBundle && !categorized;
  const enforced = !!integration.paymentEnforced || (combined && !!vialsIntegration?.paymentEnforced);
  const accountId = useAuthStore((s) => s.accountId);

  const [qty, setQty] = useState<Record<number, number>>({});
  /** Ship-to state as the order form last reported it; undefined = not reported yet. */
  const [liveShipState, setLiveShipState] = useState<string | null | undefined>(undefined);
  useEffect(() => { if (!isOpen) setLiveShipState(undefined); }, [isOpen]);

  /**
   * The clinic's own timezone, for displaying the cut-off.
   *
   * The FACILITY's, not the browser's. A coordinator working from home in
   * another state, or a laptop whose clock was never set, would otherwise be
   * shown a deadline that is not their clinic's — and the deadline is the whole
   * point. `ianaTimezone` is resolved server-side through the legacy fallback
   * chain (facility → account default), so it is null only when neither
   * resolves; the browser is the last resort, not the first.
   */
  const { data: clinicFacilities } = useQuery({
    queryKey: ['facilities', 'sla-timezone'],
    queryFn: () => listFacilities(),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });
  const { data: slaPatient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => getPatient(patientId),
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const clinicTimeZone = useMemo(() => {
    const fid = Number((slaPatient as { facilityId?: number } | undefined)?.facilityId ?? 0);
    const match = (clinicFacilities ?? []).find((f) => Number(f.facilityId) === fid);

    return match?.ianaTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [clinicFacilities, slaPatient]);

  /**
   * "Clinic — <facility>" entries in the Ship to menu, as on Partell: the
   * package goes to one of the practice's locations instead of the patient's
   * home. The patient's own facility is listed first.
   *
   * Axiom's order API has no recipient-name or ship-to-clinic field — the
   * shipment goes to `patient.address` under the patient's name — so the
   * clinic is named on the address itself ("c/o Vegas Clinic" as line 2).
   * The label then reads "Jane Doe / c/o Vegas Clinic / 123 Main St", which is
   * how the front desk knows whose package it is.
   *
   * Picking one fills the ship-to state too, so the per-state product lock
   * follows the clinic's state, not the patient's.
   */
  const shipToChoices = useMemo<ShipToChoice[]>(() => {
    const pt = slaPatient as { firstName?: string; lastName?: string; facilityId?: number } | undefined;
    const patientName = [pt?.firstName, pt?.lastName].filter(Boolean).join(' ').trim();
    const own = Number(pt?.facilityId ?? 0);
    return (clinicFacilities ?? [])
      .filter((f) => f.active !== false)
      .map((f) => {
        const name = (f.facilityName ?? '').trim() || 'Clinic';
        const line1 = (f.address ?? '').trim();
        const city = (f.city ?? '').trim();
        const state = (f.state ?? '').trim();
        const zip = (f.zip ?? '').trim();
        const complete = !!(line1 && city && state && zip);
        return {
          own: Number(f.facilityId) === own,
          complete,
          choice: {
            key: `clinic:${f.facilityId}`,
            label: `Clinic — ${name}${complete ? `, ${line1}, ${city}, ${state} ${zip}` : ' (address incomplete)'}`,
            address: {
              recipientName: [patientName, name].filter(Boolean).join(' c/o '),
              line1,
              line2: `c/o ${name}`,
              city,
              state,
              zip,
            },
            incompleteReason: complete
              ? null
              : `${name}'s address is incomplete — complete it under Admin → Facilities, or pick another address.`,
          } satisfies ShipToChoice,
        };
      })
      // An incomplete location is only worth listing when it is the patient's
      // own — staff would expect to see it, and the reason says what's missing.
      .filter((c) => c.complete || c.own)
      .sort((a, b) => Number(b.own) - Number(a.own) || a.choice.label.localeCompare(b.choice.label))
      .map((c) => c.choice);
  }, [clinicFacilities, slaPatient]);

  /**
   * The arrival estimate, re-read on a clock rather than on renders.
   *
   * Computing it once per render is not enough: a form filled slowly can be
   * OPENED at 1:58 and SUBMITTED at 2:05, and with no state change in between
   * React has no reason to re-render — so the line would still promise
   * tomorrow while the order has already slipped a day. The whole point is the
   * moment it flips, so it ticks.
   *
   * Once a minute, and only while the modal is open. That is precise enough for
   * a deadline measured in hours and cheap enough to ignore.
   */
  const [slaNow, setSlaNow] = useState(() => new Date());
  useEffect(() => {
    if (!isOpen) return;
    setSlaNow(new Date());
    const t = window.setInterval(() => setSlaNow(new Date()), 60_000);

    return () => window.clearInterval(t);
  }, [isOpen]);
  // Ship date only — overnight shipping is no longer offered, so there is no
  // arrival date to promise (see shippingSla.ts).
  const slaText = shippingSlaText(slaNow, clinicTimeZone);
  // How many cartridges of each peptide were picked from the "Pen + Cartridge"
  // list, keyed by integrationProductId.
  //
  // A COUNT, not a flag, and deliberately a SECOND map beside `qty` rather than
  // a replacement for it. Providers order a pen with one cartridge and extra
  // cartridges as refills in the same visit, so the same SKU has to be able to
  // sit in both lists at once. `qty` stays the TOTAL of the product — an order
  // payload can carry a given product only once — and this records how many of
  // that total ship with a pen. Two rows on screen, one line on the wire:
  // 1 with-pen + 1 refill of BPC-157 sends 2 cartridges and 1 pen, which is
  // exactly what the pharmacy needs to pick.
  const [penPicks, setPenPicks] = useState<Record<number, number>>({});
  /** Cartridges of this product picked from the Pen + Cartridge list. */
  const penCountOf = (id: number) => penPicks[id] ?? 0;
  /** Cartridges of this product picked from the Cartridge Only list. */
  const refillCountOf = (id: number) => Math.max(0, (qty[id] ?? 0) - penCountOf(id));
  /**
   * Set ONE half of a product's quantity and leave the other half standing.
   * Keeping `qty` as the total is what lets the two rows share a single payload
   * line; writing it directly from either list would clobber the other's pick.
   */
  const setListQty = (id: number, penRow: boolean, n: number) => {
    const pens = penRow ? Math.max(0, n) : penCountOf(id);
    const refills = penRow ? refillCountOf(id) : Math.max(0, n);
    setPenPicks((m) => ({ ...m, [id]: pens }));
    setQty((m) => ({ ...m, [id]: pens + refills }));
  };
  // Which pen section is open. Pen + Cartridge starts expanded because it is the
  // first-order default; refills are the deliberate second choice.
  // Both bundle containers start CLOSED, matching the vials picker: the modal
  // opens as a scannable list of headers rather than a long expanded catalog,
  // and each header already reports how many of its items are in the cart.
  const [penSectionOpen, setPenSectionOpen] = useState<Record<string, boolean>>({ pen: false, refill: false });
  /** Combined cart only: which catalog section the item area shows. */
  const [combinedSection, setCombinedSection] = useState<CombinedSection>('pens');
  /**
   * Default the syringes a vial cannot be used without — ONCE.
   *
   * Providers forget them because other vendors include them in the box, and
   * the patient ends up with a vial and nothing to draw it with (SUP-000799).
   * Seeded on the first vial and never again: a provider who removes them, or
   * sets a different count, has answered the question, and re-adding on the
   * next vial would overrule them silently. Adjusting or removing is exactly
   * the flexibility the request asked to keep.
   */
  const hasVial = orderableProducts.some(
    (p) => !isShippingProduct(p) && isVialProduct(p) && (qty[p.integrationProductId] ?? 0) > 0,
  );
  const syringesSeededRef = useRef(false);
  useEffect(() => { if (!isOpen) syringesSeededRef.current = false; }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !hasVial || syringesSeededRef.current) return;
    const syringe = products.find(isDefaultSyringe);
    // One Axiom has off would sit in the cart blocking submit.
    if (!syringe || backorderedUntil(syringe)) return;

    syringesSeededRef.current = true;
    setQty((m) => (
      (m[syringe.integrationProductId] ?? 0) > 0
        ? m
        : { ...m, [syringe.integrationProductId]: DEFAULT_SYRINGE_QTY }
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasVial, products, unavailableBySku, catalogIds]);

  // Per-item directions: each selected item picks Suggested (the catalog sig) or
  // Manual (free text). Keyed by integrationProductId.
  const [itemDir, setItemDir] = useState<Record<number, { mode: 'suggested' | 'manual'; text: string }>>({});
  // Categorized picker only (vials; pens render a flat, uncollapsible list):
  // search across sections + per-section expansion. `expanded` holds explicit
  // toggles; unset sections default to collapsed, so the modal opens with the
  // whole catalog scannable as a list of section headers (57 items is far too
  // many to open at once). Searching still force-expands matching sections.
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Axiom's pharmacy only ships Monday-Thursday (weekends/holidays don't
  // count toward the cutoff) — a real constraint patients hit when they
  // expect a quick delivery and still see a multi-day wait. Both fields
  // ride along as informational order metadata (like `shippingSku` below);
  // whether Axiom's own system surfaces them to the pharmacy is unconfirmed.
  const [saturdayOk, setSaturdayOk] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  // Structured prescriber — the fulfilling pharmacy (Pharmetika) hard-requires
  // first/last name, credential, NPI and PHONE; address recommended.
  const [physicianFirst, setPhysicianFirst] = useState('');
  const [physicianLast, setPhysicianLast] = useState('');
  const [credential, setCredential] = useState('');
  const [npi, setNpi] = useState('');
  const [phone, setPhone] = useState('');
  const [dea, setDea] = useState('');
  // Which account physician (users row) the prescriber fields refer to. Picking
  // one autofills from the DB, and an NPI typed here is saved back to THAT
  // physician so it's on file for every later order. '' = typed manually.
  const [prescriberUserId, setPrescriberUserId] = useState<number | ''>('');
  // NPI verification against NPPES + whether the plan doctor already had one on
  // file (if not, a newly-entered NPI is saved back to the doctor on submit).
  const [npiStatus, setNpiStatus] = useState<'idle' | 'checking' | 'verified' | 'inactive' | 'notfound'>('idle');
  const [npiResult, setNpiResult] = useState<NpiLookupResult | null>(null);
  const [npiOnFile, setNpiOnFile] = useState(false);
  // Practice address is no longer entered here — it comes from the clinic's
  // signed onboarding enrollment (the current account) at fulfillment time.

  // Supplies quantities. Pen: how many reusable pens to add (default 0 — most
  // patients already have one; bump it when they need one). Needles: every
  // cartridge already ships with premium needles free — this is how many EXTRA
  // packs to add (default 0).
  const [penQty, setPenQty] = useState(0);
  const [needleQty, setNeedleQty] = useState(0);
  // True when a re-order seed carried a pen that we deliberately did NOT seed —
  // drives the inline note in Supplies so the dropped line isn't a surprise.
  const [penDroppedFromSeed, setPenDroppedFromSeed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Must match the useState default above. The modal instance is reused, so
      // this close-reset — not the initial state — is what the NEXT open sees;
      // leaving `pen: true` here made the section render expanded every time
      // after the first, which read as the collapse fix never having shipped.
      setQty({}); setPenPicks({}); setPenSectionOpen({ pen: false, refill: false });
      setItemDir({}); setPenDroppedFromSeed(false);
      setPhysicianFirst(''); setPhysicianLast(''); setCredential(''); setNpi(''); setPhone(''); setDea('');
      setPrescriberUserId('');
      setNpiStatus('idle'); setNpiResult(null); setNpiOnFile(false);
      setPenQty(0); setNeedleQty(0);
      setSearch(''); setExpanded({});
      setCombinedSection('pens');
    }
  }, [isOpen]);

  const { data: defaults } = useQuery({
    queryKey: ['integration-order-defaults', integration.key, patientId],
    queryFn: () => getIntegrationOrderDefaults(patientId, integration.key),
    enabled: isOpen,
    staleTime: 60_000,
  });

  // The account's physicians (Doctor/Nurse users) with their stored NPI/DEA/
  // credential/phone — the prescriber dropdown's options.
  const { data: prescribers } = useQuery({
    queryKey: ['integration-prescribers', accountId],
    queryFn: getIntegrationPrescribers,
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });

  /** Fill the prescriber fields from a physician's stored record. */
  const applyPrescriber = (p: IntegrationPrescriber) => {
    setPrescriberUserId(p.userId);
    setPhysicianFirst(p.firstName);
    setPhysicianLast(p.lastName);
    if ((PRESCRIBER_CREDENTIALS as readonly string[]).includes(p.credential)) setCredential(p.credential);
    setNpi(p.npi);
    setDea(p.dea);
    if (p.phone) setPhone(p.phone);
    setNpiOnFile(!!p.npi.trim());
  };

  // Once prescribers + plan-doctor defaults are in, pre-select the plan doctor
  // in the dropdown (matched by name) so the save-back targets the right user.
  useEffect(() => {
    if (!isOpen || prescriberUserId !== '' || !prescribers?.length || !defaults?.providerName) return;
    const wanted = defaults.providerName.trim().replace(/^dr\.?\s+/i, '').toLowerCase();
    const match = prescribers.find((p) => `${p.firstName} ${p.lastName}`.trim().toLowerCase() === wanted);
    if (match) setPrescriberUserId(match.userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prescribers, defaults]);

  /**
   * Clinic standing. Cheap, and only meaningful while the modal is open, so it
   * is fetched alongside the catalog rather than held globally. A failure
   * resolves to "unknown" and the banner simply does not render — a billing
   * lookup must never be able to block ordering.
   */
  const { data: billing } = useQuery({
    queryKey: ['axiom-billing-status'],
    queryFn: getAxiomBillingStatus,
    enabled: isOpen,
    staleTime: 60_000,
    retry: false,
  });

  /**
   * Operational notices (a SKU going backordered, a holiday changing when
   * orders ship) — info/warning banners at the top of the item list. Failure
   * resolves to "show nothing" (retry: false, no error surfaced) — a notices
   * fetch failing must never block or clutter ordering, same posture as
   * `billing` above.
   */
  const { data: primaryNoticesData } = useQuery({
    queryKey: ['axiom-notices', integrationKey],
    queryFn: () => getAxiomNotices(integrationKey),
    enabled: isOpen,
    staleTime: 60_000,
    retry: false,
  });
  const { data: vialsNoticesData } = useQuery({
    queryKey: ['axiom-notices', AXIOM_VIALS_KEY],
    queryFn: () => getAxiomNotices(AXIOM_VIALS_KEY),
    enabled: isOpen && combined,
    staleTime: 60_000,
    retry: false,
  });
  // Combined cart: both modules' notices, each shown once (the two modules can
  // be served the same Axiom announcement).
  const noticesData = useMemo(() => {
    if (!combined) return primaryNoticesData;
    const seen = new Set<string>();
    const notices: AxiomNotice[] = [];
    for (const n of [...(primaryNoticesData?.notices ?? []), ...(vialsNoticesData?.notices ?? [])]) {
      const id = String(n.id);
      if (seen.has(id)) continue;
      seen.add(id);
      notices.push(n);
    }
    return { notices };
  }, [combined, primaryNoticesData, vialsNoticesData]);
  const generalNotices = (noticesData?.notices ?? []).filter((n) => !n.skus || n.skus.length === 0);
  /** Item-specific notices (carry a sku list), matched the same case-insensitive way as the rest of the catalog. */
  const noticesBySku = useMemo(() => {
    const m: Record<string, AxiomNotice[]> = {};
    for (const n of noticesData?.notices ?? []) {
      for (const sku of n.skus ?? []) {
        const key = sku.trim().toLowerCase();
        if (!key) continue;
        (m[key] ??= []).push(n);
      }
    }
    return m;
  }, [noticesData]);
  const noticesFor = (p: { metadata?: { sku?: string } | null; key: string }): AxiomNotice[] =>
    noticesBySku[(p.metadata?.sku ?? p.key ?? '').trim().toLowerCase()] ?? [];

  useEffect(() => {
    if (!isOpen || !defaults) return;
    if (!physicianFirst && !physicianLast && defaults.providerName) {
      const parts = defaults.providerName.trim().replace(/^dr\.?\s+/i, '').split(/\s+/);
      setPhysicianFirst(parts[0] ?? '');
      setPhysicianLast(parts.slice(1).join(' '));
    }
    if (!npi)   setNpi(defaults.providerNpi ?? '');
    if (!dea)   setDea(defaults.providerDea ?? '');
    if (!phone) setPhone(defaults.providerPhone ?? '');
    // Credential only prefills when it's one of the dropdown's values.
    if (!credential && (PRESCRIBER_CREDENTIALS as readonly string[]).includes(defaults.providerCredential)) {
      setCredential(defaults.providerCredential);
    }
    // Remember whether the plan doctor already had an NPI — if not, a number
    // entered here is saved back to the doctor on submit. (Practice address is no
    // longer collected here; it comes from the clinic's onboarding enrollment.)
    setNpiOnFile(!!(defaults.providerNpi ?? '').trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults, isOpen]);

  // Verify the NPI against NPPES (debounced) so staff see whether it's reachable
  // before ordering — the pharmacy needs a real, active NPI.
  useEffect(() => {
    const digits = (npi ?? '').replace(/\D/g, '');
    if (digits.length !== 10) { setNpiStatus('idle'); setNpiResult(null); return; }
    setNpiStatus('checking');
    const t = setTimeout(async () => {
      try {
        const r = await lookupNpi(digits);
        setNpiResult(r);
        setNpiStatus(!r.found ? 'notfound' : (r.active ? 'verified' : 'inactive'));
      } catch {
        setNpiStatus('notfound'); setNpiResult(null);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [npi]);

  // Prefill from an existing order when opened via Edit. Declared after the
  // defaults effect so the order's own prescriber wins over the plan default.
  // Submitting re-uses the create endpoint (POST) → a revised order; there is
  // no order-update endpoint.
  useEffect(() => {
    if (!isOpen || !prefillSource) return;
    const m = (prefillSource.metadata ?? {}) as Record<string, unknown>;
    const lineItems = Array.isArray(m.lineItems) ? (m.lineItems as Array<Record<string, unknown>>) : [];
    // Re-order only: the pen is a durable device the patient already owns, so
    // it's never carried over (sending another overcharges them and ships a
    // duplicate). Resolved from the live catalog by product id — a line whose
    // product is no longer in the catalog is left alone (it can't be a pen we
    // can identify, and submit ignores ids absent from `products` anyway).
    // Needles and every cartridge are consumables and DO carry over.
    const isSeededPen = (pid: number) => {
      const p = products.find((x) => x.integrationProductId === pid);
      return !!p && isPenProduct(p);
    };
    let droppedPen = false;
    const seeded: Record<number, number> = {};
    for (const li of lineItems) {
      const pid = Number(li.integrationProductId);
      if (pid <= 0) continue;
      if (isReorderSeed && isSeededPen(pid)) { droppedPen = true; continue; }
      // The bundled pen is an implicit line derived from the with-pen picks
      // below, never a row in the picker. Seeding it as a quantity would put a
      // product the list can't render into the cart, where it would then be
      // asked for directions it can't be given.
      const bundled = products.find((x) => x.integrationProductId === pid);
      if (bundled && isPenBundleProduct(bundled)) continue;
      seeded[pid] = Math.max(1, Number(li.quantity) || 1);
    }
    if (Object.keys(seeded).length) setQty(seeded);
    // Restore which cartridges were bought WITH a pen. Only an edit keeps them:
    // a re-order is a refill by definition (the pen was dropped just above), so
    // its cartridges come back in the Cartridge-only list.
    const seededPens: Record<number, number> = {};
    if (!isReorderSeed) {
      const ids = (m.addOn ?? {}) as Record<string, unknown>;
      // Preferred: the per-product split, which is the only shape that can say
      // "2 cartridges, 1 of them with a pen".
      const byProduct = (ids.penBundleByProduct ?? null) as Record<string, unknown> | null;
      if (byProduct && typeof byProduct === 'object') {
        for (const [raw, n] of Object.entries(byProduct)) {
          const pid = Number(raw);
          const count = Math.max(0, Number(n) || 0);
          if (pid > 0 && seeded[pid] && count > 0) seededPens[pid] = Math.min(count, seeded[pid]);
        }
      } else {
        // Orders written before the split could only be all-or-nothing, so every
        // cartridge of a listed product came with a pen.
        const penned = Array.isArray(ids.penBundleProductIds) ? ids.penBundleProductIds : [];
        for (const raw of penned) {
          const pid = Number(raw);
          if (pid > 0 && seeded[pid]) seededPens[pid] = seeded[pid];
        }
      }
    }
    if (Object.keys(seededPens).length) setPenPicks(seededPens);
    // Per-item directions: prefer the saved itemDirections map; fall back to the
    // legacy order-level `directions` applied to every line as Manual.
    const savedDirs = (m.itemDirections ?? {}) as Record<string, unknown>;
    const seededDir: Record<number, { mode: 'suggested' | 'manual'; text: string }> = {};
    for (const li of lineItems) {
      const pid = Number(li.integrationProductId);
      if (pid <= 0) continue;
      if (isReorderSeed && isSeededPen(pid)) continue; // dropped above — no directions to seed
      const v = savedDirs[String(pid)];
      if (typeof v === 'string') seededDir[pid] = { mode: 'manual', text: v };
      else if (v && typeof v === 'object') {
        const o = v as Record<string, unknown>;
        seededDir[pid] = { mode: o.mode === 'suggested' ? 'suggested' : 'manual', text: String(o.text ?? '') };
      } else if (typeof m.directions === 'string' && m.directions.trim()) {
        seededDir[pid] = { mode: 'manual', text: m.directions };
      }
    }
    if (Object.keys(seededDir).length) setItemDir(seededDir);
    const phys = (m.physician ?? {}) as Record<string, unknown>;
    const pFirst = String(phys.firstName ?? '');
    const pLast = String(phys.lastName ?? '');
    if (pFirst || pLast) {
      setPhysicianFirst(pFirst);
      setPhysicianLast(pLast);
    } else if (phys.name) {
      const parts = String(phys.name).trim().replace(/^dr\.?\s+/i, '').split(/\s+/);
      setPhysicianFirst(parts[0] ?? '');
      setPhysicianLast(parts.slice(1).join(' '));
    }
    if (phys.credential) setCredential(String(phys.credential));
    if (phys.npi)   setNpi(String(phys.npi));
    if (phys.phone) setPhone(String(phys.phone));
    if (phys.dea)   setDea(String(phys.dea));
    if (phys.userId) setPrescriberUserId(Number(phys.userId) || '');
    setSaturdayOk(m.shippingSaturdayOk === true);
    setSpecialInstructions(typeof m.specialDeliveryInstructions === 'string' ? m.specialDeliveryInstructions : '');
    const ao = (m.addOn ?? {}) as Record<string, unknown>;
    // New orders store penQty/needleQty; fall back to the legacy boolean shape.
    // The pen quantity is the second way a pen can ride along (the Supplies
    // stepper rather than a line item) — a re-order skips it for the same
    // reason, leaving the stepper at its default 0. An edit keeps the order's
    // own quantity.
    const seededPenQty = typeof ao.penQty === 'number'
      ? ao.penQty
      : (typeof ao.hasPen === 'boolean' ? (ao.hasPen ? 0 : 1) : null);
    if (isReorderSeed) {
      if ((seededPenQty ?? 0) > 0) droppedPen = true;
    } else if (seededPenQty !== null) {
      setPenQty(seededPenQty);
    }
    setPenDroppedFromSeed(droppedPen);
    if (typeof ao.needleQty === 'number') setNeedleQty(ao.needleQty);
    else if (typeof ao.extraNeedleQty === 'number') setNeedleQty(ao.extraNeedleQty);
    else if (typeof ao.extraNeedles === 'boolean') setNeedleQty(ao.extraNeedles ? 1 : 0);
    else if (ao.needleType === 'premium') setNeedleQty(1); // back-compat: older orders stored a needleType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prefillSource]);

  // SHIPPING is required (server guard SHIPPING_REQUIRED / SHIPPING_DUPLICATE)
  // but is not a choice (SCRUM-2023): the one general fee is always on the
  // order at quantity 1 and every other SHIPPING row at 0. Declared after the
  // prefill effect so it also normalises a seeded selection — a re-order or
  // clone of an old order that carried the Cold line gets the general fee
  // instead; an edit keeps the line it has. The pharmacy decides cold vs not.
  useEffect(() => {
    if (!isOpen || !defaultShipping) return;
    setQty((m) => {
      // An EDIT keeps the shipping line the order already has (#1291 review):
      // swapping an older order's Cold line for the default would re-price an
      // order that may already be paid, and nothing would tell staff. Only a
      // new order, re-order or clone takes the general fee.
      const held = editOrder ? shippingProducts.filter((s) => (m[s.integrationProductId] ?? 0) > 0) : [];
      const keep = held.length === 1 ? held[0] : defaultShipping;
      let changed = false;
      const next = { ...m };
      for (const s of shippingProducts) {
        const want = s.integrationProductId === keep.integrationProductId ? 1 : 0;
        if ((next[s.integrationProductId] ?? 0) !== want) { next[s.integrationProductId] = want; changed = true; }
      }
      return changed ? next : m;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, products, qty]);

  // Stable refs so config.submit never stales.
  const qtyRef = useRef(qty); qtyRef.current = qty;
  const itemDirRef = useRef(itemDir); itemDirRef.current = itemDir;
  const physicianFirstRef = useRef(physicianFirst); physicianFirstRef.current = physicianFirst;
  const physicianLastRef = useRef(physicianLast); physicianLastRef.current = physicianLast;
  const credentialRef = useRef(credential); credentialRef.current = credential;
  const npiRef = useRef(npi); npiRef.current = npi;
  const phoneRef = useRef(phone); phoneRef.current = phone;
  const deaRef = useRef(dea); deaRef.current = dea;
  const npiOnFileRef = useRef(npiOnFile); npiOnFileRef.current = npiOnFile;
  const prescriberUserIdRef = useRef(prescriberUserId); prescriberUserIdRef.current = prescriberUserId;
  const prescribersRef = useRef(prescribers); prescribersRef.current = prescribers;
  const penQtyRef = useRef(penQty); penQtyRef.current = penQty;
  const penPicksRef = useRef(penPicks); penPicksRef.current = penPicks;
  const needleQtyRef = useRef(needleQty); needleQtyRef.current = needleQty;
  const onOrderedRef = useRef(onOrdered); onOrderedRef.current = onOrdered;
  const onUpdatedRef = useRef(onUpdated); onUpdatedRef.current = onUpdated;
  const editOrderRef = useRef(editOrder); editOrderRef.current = editOrder;
  const enforcedRef = useRef(enforced); enforcedRef.current = enforced;
  const productsRef = useRef(products); productsRef.current = products;
  const pensProductsRef = useRef(pensProducts); pensProductsRef.current = pensProducts;
  const combinedRef = useRef(combined); combinedRef.current = combined;
  const vialsIdsRef = useRef(vialsIds); vialsIdsRef.current = vialsIds;
  const vialsShippingPriceRef = useRef(vialsIntegration?.shippingPrice ?? 0); vialsShippingPriceRef.current = vialsIntegration?.shippingPrice ?? 0;
  const onOrderedGroupRef = useRef(onOrderedGroup); onOrderedGroupRef.current = onOrderedGroup;
  const backorderedUntilRef = useRef(backorderedUntil); backorderedUntilRef.current = backorderedUntil;
  const statesBySkuRef = useRef(statesBySku); statesBySkuRef.current = statesBySku;
  const shippingPriceRef = useRef(integration.shippingPrice ?? 0); shippingPriceRef.current = integration.shippingPrice ?? 0;
  const integrationKeyRef = useRef(integrationKey); integrationKeyRef.current = integrationKey;
  const saturdayOkRef = useRef(saturdayOk); saturdayOkRef.current = saturdayOk;
  const specialInstructionsRef = useRef(specialInstructions); specialInstructionsRef.current = specialInstructions;

  const config = useMemo<LabOrderConfig>(() => ({
    vendor: integrationKeyRef.current,
    title: 'Order Peptides',
    // "Created" not "Submitted": the order is saved but still needs payment
    // before it's sent to the pharmacy.
    successTitle: 'Order Created',
    requireAddress: true,
    lockPatientIdentity: true,
    // SCRUM-1873: an "Other" chart picks Female/Male for the order only; the
    // server sends metadata.patientSex when the chart has no female/male.
    orderGenderForOther: true,
    // SCRUM-2007: no order for a patient whose chart DOB is missing or makes
    // them under 13 (or over 113) — the DOB unlocks so it can be fixed here.
    patientAgeRange: { min: 13, max: 113 },
    // Drives the live per-item state lock (see isStateLocked).
    onShipStateChange: (st: string) => setLiveShipState(toStateCode(st)),
    // Axiom's pharmacy network is licensed in all US states as of 2026-08-03
    // (confirmed directly by the vendor) — no more client-side state gate;
    // same as SiPhox and every other ship-to-patient integration, which
    // never had one. Axiom's own API remains the backstop if that ever
    // changes (ship_state_not_licensed, surfaced via AxiomIntegration's
    // friendlyError()).
    features: { presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false, notes: false, collectionDate: false, vitals: false },
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const currentQty = qtyRef.current;
      const currentProducts = productsRef.current;
      const selected = currentProducts.filter((p) => (currentQty[p.integrationProductId] ?? 0) > 0);
      // Shipping-category items are always on the order (a fee, not a pick) — only
      // real items count toward "did the staff pick anything", and shipping is
      // exempt from directions.
      const selNonShipping = selected.filter((p) => !isShippingProduct(p));
      // Effective directions per item: Suggested → the catalog sig, Manual → the
      // typed text. Defaults to Suggested when the item hasn't been touched.
      const effDirFor = (p: typeof selected[number]) => {
        const d = itemDirRef.current[p.integrationProductId];
        const sig = suggestedDirections(p);
        // Typed over several lines for readability; sent as one line (see oneLineSig).
        return oneLineSig((!d || d.mode === 'suggested') ? sig : d.text);
      };
      const firstName = physicianFirstRef.current.trim();
      const lastName = physicianLastRef.current.trim();
      const cred = credentialRef.current.trim().replace(/\./g, ''); // "M.D." → "MD"
      const npiDigits = (npiRef.current ?? '').replace(/\D/g, '');
      const phoneVal = phoneRef.current.trim();
      const deaVal = deaRef.current.trim();

      // Supplies (pen / needles) can be ordered on their own — allow a submit with
      // no peptide as long as the patient is buying at least one supply.
      // Add-ons are pens-module products; in combined mode the merged list also
      // holds the vials catalog, which must never be mistaken for one.
      const addOnSource = pensProductsRef.current;
      const wantsPen = penQtyRef.current > 0 && !!findPenAddOn(addOnSource);
      const wantsNeedles = needleQtyRef.current > 0 && !!premiumNeedleUpgrade(addOnSource);
      if (selNonShipping.length === 0 && !wantsPen && !wantsNeedles) {
        throw new Error('Select a peptide, or add a pen or needles, to place an order.');
      }
      // Per-product state licensing, checked against what was actually typed
      // (payload.state) — authoritative, unlike the best-effort inline badge
      // above, which only has data on an edit/clone. Order creation itself is
      // synchronous, but the vendor submission that would otherwise catch
      // this runs later on a delay (SubmitAxiomOrderJob), so without this a
      // staff member placing a brand-new order would not learn it failed
      // until minutes or hours afterward. The server's own fulfill() gate
      // remains the backstop for anything typed after this check runs.
      const shipState = (payload.state ?? '').trim().toUpperCase();
      if (shipState) {
        const statesBySkuNow = statesBySkuRef.current;
        const statesForNow = (p: typeof selected[number]) =>
          statesBySkuNow[(p.metadata?.sku ?? p.key ?? '').trim().toLowerCase()] ?? null;
        const stateBlocked = selNonShipping.filter((p) => {
          const states = statesForNow(p);
          return states !== null && states.length > 0 && !states.includes(shipState);
        });
        if (stateBlocked.length) {
          throw new Error(
            `Not available in ${shipState}: ${stateBlocked.map((p) => p.name).join(', ')}. `
            + 'Choose a different item, or ship to a different state.',
          );
        }
      }
      // Combined cart: extra needle packs ride as ordinary quantities from the
      // Supplies section — devices, so no directions (same as the pen/needle
      // steppers, which never reach this list).
      const missingDir = selNonShipping.filter(
        (p) => !isSupplyProduct(p) && !(combinedRef.current && isAddOnProduct(p)) && !effDirFor(p),
      );
      if (missingDir.length) throw new Error(`Directions are required for: ${missingDir.map((p) => p.name).join(', ')}.`);
      // Per-item directions — the authoritative map. The order-level
      // back-compat `directions` is derived per order in buildOrder below.
      const itemDirections: Record<number, string> = {};
      for (const p of selNonShipping) itemDirections[p.integrationProductId] = effDirFor(p);
      if (!firstName || !lastName) throw new Error('Prescriber first and last name are required.');
      if (!cred) throw new Error(`Prescriber credential (${PRESCRIBER_CREDENTIALS.join('/')}) is required.`);
      if (npiDigits.length < 10) throw new Error('NPI must be at least 10 digits.');
      if (!phoneVal) throw new Error("Prescriber phone is required — the pharmacy can't fill the order without it.");

      // ── Supplies ─────────────────────────────────────────────────────────
      // Pen: add `penQty` reusable pens (0 = the patient already has one).
      // Needles: premium needles already ship free with each cartridge; add
      // `needleQty` EXTRA packs on top (0 = none). Both are explicit quantities.
      let effectiveSelected = selected;
      let penProvided = false;
      let penProduct: typeof selected[number] | undefined;
      const penCount = penQtyRef.current;
      let needleProduct: typeof selected[number] | undefined;
      const needleCount = needleQtyRef.current;
      const inCart = (id: number) => effectiveSelected.some((p) => p.integrationProductId === id);

      // Pen: a one-time purchase, addable with or without a peptide.
      if (penCount > 0) {
        const pen = findPenAddOn(addOnSource);
        if (pen && !inCart(pen.integrationProductId)) {
          penProvided = true;
          penProduct = pen;
          effectiveSelected = [...effectiveSelected, pen];
        }
      }

      // Bundled pens: one per cartridge picked from the "Pen + Cartridge" list.
      // Summed across peptides into a single line (an order can carry a given
      // product only once), so 2 × BPC-157 + 1 × NAD+ with pens ships 3 pens.
      // The bundle row's metadata.sku is the ordinary pen SKU, so the pharmacy
      // receives the cartridges and the pens it already knows how to fulfil.
      const pennedProducts = selNonShipping.filter((p) => (penPicksRef.current[p.integrationProductId] ?? 0) > 0);
      const bundlePenCount = pennedProducts.reduce(
        (n, p) => n + (penPicksRef.current[p.integrationProductId] ?? 0),
        0,
      );
      let bundleProduct: typeof selected[number] | undefined;
      if (bundlePenCount > 0) {
        const bundle = findPenBundleAddOn(addOnSource);
        if (!bundle) {
          return {
            ok: false,
            lines: [],
            errorMessage: 'The pen bundle is not configured for this integration — order the cartridges as refills, or add a pen under Supplies.',
          };
        }
        bundleProduct = bundle;
        if (!inCart(bundle.integrationProductId)) {
          effectiveSelected = [...effectiveSelected, bundle];
        }
      }

      // Extra needle packs — the exact quantity the staff chose.
      if (needleCount > 0) {
        const extra = premiumNeedleUpgrade(addOnSource);
        if (!extra) {
          return { ok: false, lines: [], errorMessage: 'Extra needles are not configured for this integration.' };
        }
        needleProduct = extra;
        if (!inCart(extra.integrationProductId)) {
          effectiveSelected = [...effectiveSelected, extra];
        }
      }
      // Stock gate: block submit while any item in the cart (peptides and
      // supplies alike) is out of stock per the live catalog. The server
      // re-checks at send time, but failing here is friendlier.
      const oos = effectiveSelected
        .map((p) => ({ p, until: backorderedUntilRef.current(p) }))
        .filter((x): x is { p: typeof effectiveSelected[number]; until: string | true } => !!x.until);
      if (oos.length) {
        throw new Error(
          `Out of stock: ${oos.map((x) => (x.until === true ? x.p.name : `${x.p.name} (back-ordered until ${fmtBackorderDate(x.until)})`)).join(', ')}. Remove the item to place the order.`,
        );
      }

      // Per-product order quantity: the pen and needle add-ons use their chosen
      // quantities; every other item uses its own picker quantity (default 1).
      const qtyFor = (p: typeof selected[number]) =>
        isShippingProduct(p)
          ? 1 // shipping quantity is locked to 1 (server rejects >1 unit)
          : needleProduct && p.integrationProductId === needleProduct.integrationProductId
            ? needleCount
            : penProduct && p.integrationProductId === penProduct.integrationProductId
              ? penCount
              : bundleProduct && p.integrationProductId === bundleProduct.integrationProductId
                ? bundlePenCount
                : (currentQty[p.integrationProductId] ?? 1);

      // NOTE: we deliberately do NOT write the delivery address back to the
      // patient's profile. The address entered here is the SHIP-TO for this
      // order only (it rides in orderMeta.shippingAddress and, if the staff
      // chose to, the patient's shipping address book) — editing it must never
      // overwrite the patient's chart address. Identity (name/DOB) is read-only
      // and matches the chart already, so there's nothing to sync here.

      // Persist the NPI on the physician so it's on file for every later order.
      // A selected prescriber (dropdown) is the explicit target — save whenever
      // the entered NPI differs from what's stored. With no selection, fall back
      // to the legacy behavior (plan doctor, only when they had none on file).
      // Best-effort — never blocks the order.
      const presId = prescriberUserIdRef.current;
      if (npiDigits.length === 10) {
        try {
          if (presId) {
            const stored = (prescribersRef.current?.find((p) => p.userId === presId)?.npi ?? '').replace(/\D/g, '');
            if (stored !== npiDigits) {
              await saveIntegrationProviderNpi(payload.patientId, integrationKeyRef.current, npiDigits, presId);
            }
          } else if (!npiOnFileRef.current) {
            await saveIntegrationProviderNpi(payload.patientId, integrationKeyRef.current, npiDigits);
          }
        } catch { /* non-fatal: the order still proceeds with the entered NPI */ }
      }

      // One order's payload from a group of the cart's products. Outside
      // combined mode the group is the whole cart, so this builds exactly the
      // single order it always did; a combined cart builds one per module.
      const NO_ADD_ON = {
        penQty: 0, penProvided: false, needleQty: 0, needleSku: null,
        penBundleQty: 0, penBundleProductIds: [] as number[], penBundleByProduct: {},
      };
      const buildOrder = (
        group: typeof effectiveSelected,
        opts: { withAddOn: boolean; shippingFee: number },
      ) => {
        const ids = new Set(group.map((p) => p.integrationProductId));
        // Only THIS order's items: the vials order must not carry the pens'
        // directions (or vice versa). Order-level directions = the first of
        // this group's items, as the single-order form always did.
        const groupItemDirections: Record<number, string> = {};
        for (const [id, text] of Object.entries(itemDirections)) {
          if (ids.has(Number(id))) groupItemDirections[Number(id)] = text;
        }
        const firstInGroup = selNonShipping.find((p) => ids.has(p.integrationProductId));
        const groupDirs = firstInGroup ? effDirFor(firstInGroup) : '';
        const items: OrderItemSelection[] = group.map((p) => ({
          integrationProductId: p.integrationProductId,
          quantity: qtyFor(p),
        }));
        const prefillItems: ChargePaymentPrefillItem[] = group.map((p) => ({
          quoteItemCostId: p.quoteItemCostId,
          quantity: qtyFor(p),
          unitPrice: p.price,
          // The fee line reads "Shipping" on the charge, never the catalog
          // row's name (no "Cold"/"Standard" — the pharmacy decides).
          productTypeName: isShippingProduct(p) ? SHIPPING_LABEL : p.name,
        }));
        // Shipping — a flat per-order fee the server adds to the charge. Include it
        // as its own line so an enforced (collect-first) checkout collects it too,
        // matching order.amount. Integration lines all share one catalog quote item
        // and differ by name/price, so reuse that id for the shipping line.
        const shippingFee = group.length > 0 ? opts.shippingFee : 0;
        if (shippingFee > 0 && group[0]?.quoteItemCostId != null) {
          prefillItems.push({
            quoteItemCostId: group[0].quoteItemCostId,
            quantity: 1,
            unitPrice: shippingFee,
            productTypeName: SHIPPING_LABEL,
          });
        }
        const metadata = {
          // SCRUM-1873: sex for this order when the chart says neither (Other).
          ...(payload.orderSex ? { patientSex: payload.orderSex } : {}),
          directions: groupDirs, // order-level, back-compat fallback
          itemDirections: groupItemDirections, // authoritative per-item directions (productId → text)
          physician: {
            ...(presId ? { userId: presId } : {}), // which physician user this prescriber is
            firstName,
            lastName,
            name: `${firstName} ${lastName}`.trim(), // combined kept for back-compat
            credential: cred,
            npi: npiDigits,
            phone: phoneVal,
            ...(deaVal ? { dea: deaVal } : {}),
            // Practice address intentionally omitted — the backend fills it from the
            // clinic's signed onboarding enrollment (the current account's address).
          },
          // Record how the pen + needle requirements were satisfied for this order.
          addOn: !opts.withAddOn ? NO_ADD_ON : {
            penQty: penProduct ? penCount : 0,
            penProvided,
            needleQty: needleProduct ? needleCount : 0,
            needleSku: needleProduct?.metadata?.sku ?? needleProduct?.key ?? null,
            // Bundled pens: the total shipped, and which cartridges asked for one.
            // The id list is what lets an edit reopen with the same cartridges in
            // the Pen + Cartridge list rather than silently demoting them to refills.
            penBundleQty: bundleProduct ? bundlePenCount : 0,
            penBundleProductIds: pennedProducts.map((p) => p.integrationProductId),
            // The per-product split. The id list above cannot express "2
            // cartridges, 1 with a pen", so an edit reopened from it alone would
            // put both cartridges back in the Pen + Cartridge list.
            penBundleByProduct: Object.fromEntries(
              pennedProducts.map((p) => [p.integrationProductId, penPicksRef.current[p.integrationProductId] ?? 0]),
            ),
          },
          // Include per-item protocol info so the order record and patient view
          // can display compound, dose, and frequency without re-fetching the catalog.
          itemProtocols: group.map((p) => {
            const device = isAddOnProduct(p);
            const deviceType = addOnType(p);
            return {
              name: p.name,
              // Snapshot the brand alongside the name so a historical order renders
              // the way it did when placed, even if the catalog is renamed later.
              ...(brandNameOf(p) ? { brandName: brandNameOf(p) } : {}),
              sku: p.metadata?.sku ?? p.key,
              // Mark DEVICES (pen, bundled pen, needle tips) as such. Downstream
              // renderers — the patient order emails above all — must show a device
              // as name + quantity only: it has no concentration and no dose, and a
              // "YOUR INSTRUCTIONS: INJECT 20 units" line under a PEN is wrong and
              // frightening. Without this marker the notifier has to fall back to a
              // catalog lookup by SKU, which is what it does for orders placed
              // before this snapshot existed.
              ...(device ? { isAddOn: true } : {}),
              ...(deviceType ? { addOnType: deviceType } : {}),
              strength: p.metadata?.strength ?? null,
              recommendedDose: p.metadata?.recommendedDose ?? null,
              frequency: p.metadata?.frequency ?? null,
              injectionsPerCart: p.metadata?.injectionsPerCart ?? null,
              weeksPerCart: p.metadata?.weeksPerCart ?? null,
              quantity: qtyFor(p),
              // Devices inherit NO directions. `itemDirections` only ever holds the
              // real (non-shipping, non-add-on) items staff typed a sig for, so
              // without this an add-on silently picked up `dirs` — the FIRST
              // peptide's sig — and the pen ended up carrying the peptide's dosing.
              directions: itemDirections[p.integrationProductId]
                ?? ((isShippingProduct(p) || isSupplyProduct(p) || device) ? '' : groupDirs),
              // Which of the two the prescriber picked. The patient email must
              // never print the catalog's "INJECT 12 units" over directions a
              // provider typed, and comparing text to the catalog can only ever
              // infer that — this states it. Devices and shipping lines have no
              // dosing to qualify, so they carry no mode.
              ...(isShippingProduct(p) || isSupplyProduct(p) || device
                ? {}
                : { directionsMode: dirModeOf(p) }),
            };
          }),
          shippingAddress: {
            recipientName: payload.recipientName || undefined,
            line1: payload.street,
            line2: payload.street2 || undefined,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            country: payload.country || undefined,
            phone: payload.phone || undefined,
            // Saved-address row the staff picked; the backend upserts and
            // overwrites this with the authoritative id either way.
            patientShippingAddressId: payload.shippingAddressId ?? undefined,
            // Axiom has no ship-to-clinic field (the clinic rides on line 2 as
            // "c/o …"), so say it outright: the clinic-handoff "Picked up" step
            // (SCRUM-2200) exists only for orders delivered to the clinic.
            ...(payload.shipToChoice?.startsWith('clinic:')
              ? { shipToClinic: true, facilityId: Number(payload.shipToChoice.slice('clinic:'.length)) || undefined }
              : {}),
          },
          shippingSaturdayOk: saturdayOkRef.current,
          specialDeliveryInstructions: specialInstructionsRef.current.trim() || undefined,
        };
        return { items, prefillItems, metadata };
      };

      // A DOB corrected in the form (SCRUM-2007) goes to the chart BEFORE the
      // order exists: Axiom reads the patient's DOB from the chart when the
      // order is sent, which for a held order is later. Only the DOB and a
      // gender the chart lacked (SCRUM-1873) — the ship-to may be a clinic,
      // and must never become the chart address.
      const { birthDate: dobFix, gender: genderFix } = payload.demographicsFill ?? {};
      const chartFix = { ...(dobFix ? { birthDate: dobFix } : {}), ...(genderFix ? { gender: genderFix } : {}) };
      if (Object.keys(chartFix).length > 0) await updatePatient(payload.patientId, chartFix);

      // Edit: update the existing order in place (PATCH, same endpoint family).
      // No re-payment and no re-fulfillment — a local correction of the record.
      const editing = editOrderRef.current;
      if (editing) {
        const { items, metadata } = buildOrder(effectiveSelected, {
          withAddOn: true,
          shippingFee: shippingPriceRef.current ?? 0,
        });
        const updated = await updateIntegrationOrder(payload.patientId, editing.integrationOrderId, { key: integrationKeyRef.current, items, metadata });
        onUpdatedRef.current?.();
        return { ok: true, lines: [`Axiom order #${updated.integrationOrderId} updated.`] };
      }

      // Always create the order, even when payment is enforced. The server holds
      // it in 'awaiting_payment' and never sends it to the pharmacy until the fee
      // is collected, so creating it up front is safe — and it means an abandoned
      // payment leaves a visible order staff can finish paying, rather than
      // silently discarding the work.
      if (!combinedRef.current) {
        const { items, prefillItems, metadata } = buildOrder(effectiveSelected, {
          withAddOn: true,
          shippingFee: shippingPriceRef.current ?? 0,
        });
        const result = await createIntegrationOrder(payload.patientId, {
          key: integrationKeyRef.current,
          items,
          // A Re-send says which cancelled order it replaces, for the activity log.
          metadata: {
            ...metadata,
            ...(resentFromRef.current ? { resentFromOrderId: resentFromRef.current } : {}),
            ...(reorderOfRef.current ? { reorderOfOrderId: reorderOfRef.current } : {}),
          },
        });
        onOrderedRef.current(result, prefillItems);
        return {
          ok: true,
          lines: result.fulfillmentStatus === 'awaiting_payment'
            ? [`Axiom order #${result.integrationOrderId} created — collect payment to send it to the pharmacy.`]
            : [`Axiom order #${result.integrationOrderId} created — ships to the address on file.`],
        };
      }

      // ── Combined cart: split by module ─────────────────────────────────────
      // Pens (with the pen/needle add-ons) → the pens integration; everything
      // from the vials catalog → the vials integration. The vials order exists
      // only when a vials item was picked: its shipping fee is always on the
      // cart, and on its own it is not an order. (Each order keeps its own
      // fee line; useAxiomOrderFlow folds them into ONE "Shipping" line on
      // the single charge for the cart.)
      const vialsIdsNow = vialsIdsRef.current;
      const pensGroup = effectiveSelected.filter((p) => !vialsIdsNow.has(p.integrationProductId));
      const vialsGroupAll = effectiveSelected.filter((p) => vialsIdsNow.has(p.integrationProductId));
      const vialsGroup = vialsGroupAll.some((p) => !isShippingProduct(p)) ? vialsGroupAll : [];
      const planned = [
        {
          key: integrationKeyRef.current,
          label: 'pens',
          group: pensGroup,
          ...buildOrder(pensGroup, { withAddOn: true, shippingFee: shippingPriceRef.current ?? 0 }),
        },
        {
          key: AXIOM_VIALS_KEY,
          label: 'vials',
          group: vialsGroup,
          ...buildOrder(vialsGroup, { withAddOn: false, shippingFee: vialsShippingPriceRef.current ?? 0 }),
        },
      ].filter((o) => o.group.length > 0);
      // Shared only when the cart really became two orders: it is what lets the
      // one payment taken for the cart be attached to both (markOrderPaid).
      const cartGroupId = planned.length > 1 ? newCartGroupId() : null;

      const created: Array<AxiomCreatedOrder & { label: string }> = [];
      let failed: { label: string; message: string } | null = null;
      for (const o of planned) {
        try {
          const result = await createIntegrationOrder(payload.patientId, {
            key: o.key,
            items: o.items,
            metadata: cartGroupId ? { ...o.metadata, cartGroupId } : o.metadata,
          });
          created.push({ key: o.key, label: o.label, result, prefillItems: o.prefillItems });
        } catch (err) {
          // Nothing created yet: a plain failure, the form stays as it was.
          if (created.length === 0) throw err;
          // The first order EXISTS. Never report this as a failure the staff
          // would retry — that would place the first order twice. Finish
          // successfully, and say exactly which half is missing.
          failed = { label: o.label, message: getErrorMessage(err, 'The order could not be created.') };
          break;
        }
      }

      const notify = onOrderedGroupRef.current;
      if (notify) {
        notify(created.map(({ key, result, prefillItems }) => ({ key, result, prefillItems })));
      } else {
        for (const c of created) onOrderedRef.current(c.result, c.prefillItems);
      }

      const lines = created.map((c) => (c.result.fulfillmentStatus === 'awaiting_payment'
        ? `Axiom ${c.label} order #${c.result.integrationOrderId} created — collect payment to send it to the pharmacy.`
        : `Axiom ${c.label} order #${c.result.integrationOrderId} created — ships to the address on file.`));
      if (failed) {
        const first = created[0];
        const msg = `The ${failed.label} order was NOT created (${failed.message}). `
          + `The ${first.label} order #${first.result.integrationOrderId} was placed — `
          + `order the ${failed.label} items again as a new order.`;
        lines.push(msg);
        toast.error(msg);
      }
      return { ok: true, lines };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const selected = products.filter((p) => (qty[p.integrationProductId] ?? 0) > 0);
  // Shipping-category items (vials) are a fee that is always on the order —
  // exclude them from the "did the staff pick anything yet" counts so an empty
  // form doesn't claim an item is selected.
  const selectedNonShipping = selected.filter((p) => !isShippingProduct(p));
  const selectedShipping = selected.filter(isShippingProduct);
  const total = selectedNonShipping.reduce((s, p) => s + p.price * (qty[p.integrationProductId] ?? 0), 0);
  // Shipping shown so the displayed total matches what the patient is billed:
  // the flat per-order fee the server adds (pens) plus the SHIPPING fee line
  // (vials). Only applies once an order has real items.
  //
  // Combined cart: each order pays its OWN shipping — the pens order the flat
  // fee (whenever it exists, supplies-only included, as its submit bills it),
  // the vials order its SHIPPING line (whenever any vials item is picked).
  // Staff and patient only ever see the SUM, as one "Shipping" line.
  const pensOrderExists = combined && (
    selectedNonShipping.some((p) => !isVialsSourced(p)) || penQty > 0 || needleQty > 0
  );
  const vialsOrderExists = combined && selectedNonShipping.some(isVialsSourced);
  const pensShipping = pensOrderExists ? (integration.shippingPrice ?? 0) : 0;
  const vialsShipping = vialsOrderExists
    ? (vialsIntegration?.shippingPrice ?? 0) + selectedShipping.reduce((s, p) => s + p.price, 0)
    : 0;
  const shipping = combined
    ? pensShipping + vialsShipping
    : selectedNonShipping.length > 0
      ? (integration.shippingPrice ?? 0) + selectedShipping.reduce((s, p) => s + p.price, 0)
      : 0;
  // The read-only fee shown next to the ship date: what this cart pays once it
  // has items, and before that (vials form) what a vials order will pay.
  const shippingFeeShown = shipping > 0
    ? shipping
    : combined ? 0 : (integration.shippingPrice ?? 0) + (defaultShipping?.price ?? 0);

  // Supplies (pen / needles) can be ordered with or without a peptide.
  const penAddOn = findPenAddOn(pensProducts);
  const extraNeedlePack = premiumNeedleUpgrade(pensProducts);
  // Combined cart: the other needle-tip packs of the pens catalog (standard,
  // 100-count…), offered in Supplies as plain quantities. The premium pack keeps
  // its own stepper (needleQty) and the bundled pen is never picked directly.
  const otherNeedlePacks = combined
    ? needleAddOns(pensProducts).filter((p) => p.integrationProductId !== extraNeedlePack?.integrationProductId)
    : [];

  // Supplies (pen + needle packs) at their chosen quantities, and their cost.
  // Computed here (not in the confirm summary) so the running total can show it.
  const supplyLines: string[] = [];
  let supplyTotal = 0;
  if (penAddOn && penQty > 0) {
    supplyLines.push(`${penAddOn.name} × ${penQty} (${usd(penAddOn.price * penQty)})`);
    supplyTotal += penAddOn.price * penQty;
  }
  if (extraNeedlePack && needleQty > 0) {
    supplyLines.push(`${extraNeedlePack.name} × ${needleQty} (${usd(extraNeedlePack.price * needleQty)})`);
    supplyTotal += extraNeedlePack.price * needleQty;
  }
  // Bundled pens — one per cartridge picked from the Pen + Cartridge list.
  // Counted here so the running total matches what the server will bill, since
  // the bundle is a real line item priced from the catalog like any other.
  const bundledPenCount = selectedNonShipping.reduce(
    (n, p) => n + penCountOf(p.integrationProductId),
    0,
  );
  const bundledPenTotal = penBundle ? penBundle.price * bundledPenCount : 0;
  // NOT pushed onto supplyLines: the confirmation lists each with-pen pick as
  // its own item priced cartridge + pen, so repeating the pens here would show
  // their cost twice. The arithmetic below is unchanged — only where the number
  // is displayed moved.
  const grandTotal = total + supplyTotal + bundledPenTotal + shipping;

  // Per-item directions helpers for the form. Suggested → catalog sig; Manual → typed.
  const itemSig = (p: typeof products[number]) => suggestedDirections(p);
  const dirModeOf = (p: typeof products[number]) => itemDir[p.integrationProductId]?.mode ?? 'suggested';
  const dirTextOf = (p: typeof products[number]) =>
    dirModeOf(p) === 'suggested' ? itemSig(p) : (itemDir[p.integrationProductId]?.text ?? '');
  const setDirMode = (pid: number, mode: 'suggested' | 'manual') =>
    setItemDir((m) => ({ ...m, [pid]: { mode, text: m[pid]?.text ?? '' } }));
  const setDirText = (pid: number, text: string) =>
    setItemDir((m) => ({ ...m, [pid]: { mode: 'manual', text } }));

  // Price summary above Required Details (mirrors SiPhox topSlot).
  /**
   * What the patient already paid for, when this order is being placed to
   * fulfil a payment. Amber rather than the usual slate so it reads as
   * something to act on, and explicitly labelled as not selected — the one
   * misreading that matters is assuming the cart below already matches it.
   */
  const paidSlot = paidReference && paidReference.items.length > 0 ? (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-amber-900">
          Paid for{paidReference.patientName ? ` by ${paidReference.patientName}` : ''}
        </span>
        <span className="text-xs text-amber-700">
          Payment #{paidReference.paymentId}
          {paidReference.paidOn ? ` · ${new Date(paidReference.paidOn).toLocaleDateString()}` : ''}
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        {paidReference.items.map((it, i) => (
          <li key={i} className="flex items-baseline justify-between gap-3 text-sm text-amber-900">
            <span>{it.name ?? '—'}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
            <span className="tabular-nums">{usd(it.amount)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-800">
        Nothing is selected for you — these are the practice's own product names, which
        do not map to the Axiom catalog. Pick the matching items below.
      </p>
    </div>
  ) : null;

  const selectionSlot = selectedNonShipping.length > 0 ? (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
      <span className="text-sm text-slate-600">
        {selectedNonShipping.length} item{selectedNonShipping.length !== 1 ? 's' : ''} selected
        {shipping > 0 ? ' · incl. shipping' : ''}
      </span>
      <span className="text-sm font-semibold text-slate-900">Total: {usd(grandTotal)}</span>
    </div>
  ) : null;

  /**
   * The ship-to this order already carries, so reopening it shows where the
   * package is actually going (SCRUM-1814).
   *
   * Only for an EDIT. A re-order is a new order and deliberately starts from
   * the patient's current address — copying a months-old delivery address into
   * a fresh prescription is how a package goes somewhere nobody chose.
   */
  const initialShipTo = useMemo<InitialShipTo | null>(() => {
    // An edit must show the order's own address; a clone deliberately reuses
    // it. Everything else (including a plain re-order) lets the form seed from
    // the patient's addresses.
    const source = editOrder ?? shipFromOrder;
    if (!source) return null;
    const a = (source.metadata as { shippingAddress?: InitialShipTo } | undefined)?.shippingAddress;
    return a && typeof a === 'object' ? a : null;
  }, [editOrder, shipFromOrder]);

  // SCRUM-1873: the sex an edited / re-ordered order was placed with, so an
  // "Other" chart opens with that pick instead of a blank Gender (the core
  // ignores it for a female/male or blank chart). buildOrder writes the pick
  // back to metadata.patientSex on save.
  const initialOrderSex = useMemo<'F' | 'M' | null>(() => {
    const v = (prefillSource?.metadata as { patientSex?: unknown } | undefined)?.patientSex;
    return v === 'F' || v === 'M' ? v : null;
  }, [prefillSource]);

  /**
   * The ship-to state the order is going to, LIVE: LabOrderModalCore reports
   * it through config.onShipStateChange as staff type it, pick a saved
   * address, or it loads from the chart. Until the form has reported (the
   * first render), an edited/cloned order's saved state stands in.
   * config.submit still re-checks the address actually submitted.
   */
  const shipState = liveShipState !== undefined
    ? liveShipState
    : toStateCode(initialShipTo?.state);
  /** True when Axiom does not ship this item to the ship-to state. */
  const isStateLocked = (p: { metadata?: { sku?: string } | null; key: string }): boolean => {
    if (!shipState) return false;
    const states = availableStatesFor(p);
    return states !== null && states.length > 0 && !states.includes(shipState);
  };

  /**
   * Picking a destination Axiom doesn't ship an item to takes that item off
   * the order — it could never be submitted — and says so, rather than
   * leaving it checked behind a lock staff would have to notice.
   */
  useEffect(() => {
    if (!isOpen || !shipState) return;
    const locked = products.filter(
      (p) => (qty[p.integrationProductId] ?? 0) > 0 && isStateLocked(p),
    );
    if (locked.length === 0) return;
    const ids = new Set(locked.map((p) => p.integrationProductId));
    setQty((m) => Object.fromEntries(Object.entries(m).map(([id, n]) => [id, ids.has(Number(id)) ? 0 : n])));
    setPenPicks((m) => Object.fromEntries(Object.entries(m).map(([id, n]) => [id, ids.has(Number(id)) ? 0 : n])));
    toast.info(`Removed — not available in ${shipState}: ${locked.map((p) => p.name).join(', ')}`);
  // Runs when the destination or the catalog's state lists change, not on
  // every pick: a locked item can't be picked in the first place.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shipState, statesBySku]);

  /**
   * The clinic's Axiom standing, BEFORE the order is placed (SCRUM-1805).
   *
   * A late invoice pauses ordering at Axiom's end: the order is accepted here,
   * the patient is charged, and the pharmacy rejects it with `clinic_overdue`.
   * Staff used to meet that one rejected order at a time, days later, by
   * opening the order and reading the tracking column. Saying it once, here,
   * costs one line and saves the charge.
   *
   * Rendered ONLY when Axiom actually reports a late invoice. `known === false`
   * means the balance could not be read, and a clinic must never be told it is
   * in good standing on the strength of a failed lookup — nor warned on one.
   */
  const billingSlot = billing?.known && billing.lateCount > 0 ? (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
      <p className="text-sm font-semibold text-red-900">
        Ordering is paused — {billing.lateCount} overdue Axiom invoice{billing.lateCount !== 1 ? 's' : ''}
      </p>
      <p className="mt-1 text-xs text-red-800">
        Axiom rejects new orders until the balance is settled. Placing this order will
        charge the patient and then fail at the pharmacy. Settle the invoice with Axiom first.
      </p>
    </div>
  ) : null;

  const topSlot = billingSlot || paidSlot || selectionSlot ? (
    <div className="space-y-3">{billingSlot}{paidSlot}{selectionSlot}</div>
  ) : null;

  // Items + physician + directions — same position as Tests in Vibrant/GetLabs.
  const npiValid = (npi ?? '').replace(/\D/g, '').length >= 10;

  // ── Categorized picker (vials) helpers ─────────────────────────────────────
  const searchQ = search.trim().toLowerCase();
  const matchesSearch = (p: typeof products[number]) =>
    searchQ === '' ||
    p.name.toLowerCase().includes(searchQ) ||
    brandNameOf(p).toLowerCase().includes(searchQ) ||
    String(p.metadata?.sku ?? p.key).toLowerCase().includes(searchQ);
  // Sections in the fixed contractual order, unknown categories last (A→Z).
  const sections = (() => {
    if (!categorized && !combined) return [] as Array<{ category: string; products: typeof orderableProducts }>;
    const by = new Map<string, typeof orderableProducts>();
    // Combined cart: the vials catalog minus SUPPLIES (→ Supplies section).
    // SHIPPING is never a section: it is a fee, added on its own.
    for (const p of combined ? vialsPickable : orderableProducts.filter((x) => !isShippingProduct(x))) {
      const c = categoryOf(p) || 'OTHER';
      if (!by.has(c)) by.set(c, []);
      by.get(c)!.push(p);
    }
    const known = CATEGORY_ORDER.filter((c) => by.has(c));
    const unknown = [...by.keys()].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
    return [...known, ...unknown].map((c) => ({ category: c, products: by.get(c)! }));
  })();

  /** One orderable item row — checkbox, image, price/qty, protocol, directions.
   *  Shared by the flat (pens) list, the categorized (vials) sections, and both
   *  halves of the pen split. Pass `{ pen: true }` for the Pen + Cartridge list.
   *
   *  NOTE: never hand this straight to `.map()` — the second argument would be
   *  the array index, which is truthy for every row but the first. */
  const renderItemRow = (p: typeof products[number], opts?: { pen?: boolean }) => {
              const penRow = !!opts?.pen;
              // The pen split's two-halves bookkeeping applies to pens items
              // only — a vials row in the combined cart is a plain quantity.
              const splitRow = penChoice && !isVialsSourced(p);
              const q = qty[p.integrationProductId] ?? 0;
              const meta = p.metadata;
              // Both pen lists render the SAME peptides, so each row shows only
              // ITS half of the pick. The two are independent: BPC-157 can be
              // ticked with a pen here and again as a refill next door, which is
              // how a provider sends a pen plus a spare cartridge.
              const listQty = splitRow ? (penRow ? penCountOf(p.integrationProductId) : refillCountOf(p.integrationProductId)) : q;
              const isChecked = listQty > 0;
              // A pen ships with every cartridge in this list, so the row quotes
              // the pair — the same number the running total and the charge use.
              const unitPrice = penRow && penBundle ? p.price + penBundle.price : p.price;
              // Catalog sig for the protocol box — same resolution the Directions
              // block and the submitted itemDirections use (sig / dosingInstructions).
              const sig = itemSig(p);
              // One line per spec column of Axiom's sheet, tolerant of the pens
              // and vials catalogs spelling the same facts differently.
              const specs = specLines(meta);
              // Back-ordered items can't be added. One already in the cart (e.g.
              // from a re-order seed) stays uncheckable-off so staff can remove
              // it — submit is blocked while it remains.
              const oosUntil = backorderedUntil(p);
              // Same treatment for a product Axiom won't ship to the ship-to
              // state, as staff enter or pick it (see isStateLocked).
              const stateLocked = isStateLocked(p);
              const blocked = !!oosUntil || stateLocked;
              const itemNotices = noticesFor(p);
              return (
                <div key={p.integrationProductId} className="py-2.5 space-y-2">
                  <div className={`flex items-start gap-3 ${blocked && !isChecked ? 'opacity-60' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={blocked && !isChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        // Only this list's half moves; the other list keeps
                        // whatever it holds.
                        if (splitRow) setListQty(p.integrationProductId, penRow, checked ? 1 : 0);
                        else setQty((m) => ({ ...m, [p.integrationProductId]: checked ? 1 : 0 }));
                      }}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                          {brandNameOf(p) && (
                            <span className="mr-1.5 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white">
                              {brandNameOf(p)}
                            </span>
                          )}
                          <span className={blocked ? 'line-through decoration-slate-400' : undefined}>{p.name}</span>
                          {oosUntil && (
                            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                              {stockLabel(oosUntil)}
                            </span>
                          )}
                          {stateLocked && (
                            <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                              Not available in {shipState}
                            </span>
                          )}
                          {itemNotices.map((n) => (
                            <span
                              key={n.id}
                              title={n.message}
                              className={
                                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold '
                                + (n.severity === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800')
                              }
                            >
                              {n.title ?? n.message ?? 'Notice'}
                            </span>
                          ))}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          {unitPrice > 0 && (
                            <span
                              className="text-xs text-slate-500"
                              title={penRow && penBundle
                                ? `${usd(p.price)} cartridge + ${usd(penBundle.price)} pen`
                                : undefined}
                            >
                              {usd(unitPrice)}
                            </span>
                          )}
                          {isChecked && (
                            <QtyStepper
                              value={listQty}
                              min={1}
                              label={`${p.name} quantity`}
                              title={penRow
                                ? 'Quantity — number of cartridges to order WITH a pen. A pen ships with each one.'
                                : 'Quantity — number of cartridges of this item to order'}
                              onChange={(n) => (splitRow
                                ? setListQty(p.integrationProductId, penRow, n)
                                : setQty((m) => ({ ...m, [p.integrationProductId]: n })))}
                            />
                          )}
                        </div>
                      </div>
                      {/* Protocol details — shown when checked so staff can verify before
                          ordering. Suppressed entirely when the item carries neither specs
                          nor a sig, so a bare item (e.g. shipping) doesn't get an empty box. */}
                      {isChecked && (specs.length > 0 || sig !== '') && (
                        <div className="mt-1.5 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 space-y-0.5 text-[11px] text-blue-800">
                          {specs.map((l) => (
                            <p key={l.label}><span className="font-medium">{l.label}:</span> {l.value}</p>
                          ))}
                          {sig && <p className="pt-0.5 italic text-blue-700">{sig}</p>}
                        </div>
                      )}
                      {/* Per-item directions — Suggested (catalog sig) or Manual.
                          Supplies carry no dosing, so they are not asked. */}
                      {isChecked && !isSupplyProduct(p) && (
                        <div className="mt-2">
                          <div className="mb-1 flex items-center gap-2">
                            <span className="text-[11px] font-medium text-slate-600">Directions <span className="text-red-500">*</span></span>
                            <Hint text="The sig sent to the pharmacy for THIS item. Suggested uses the catalog's recommended directions; Manual lets you type your own." />
                            <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
                              <button
                                type="button"
                                onClick={() => setDirMode(p.integrationProductId, 'suggested')}
                                className={`px-2 py-0.5 text-[11px] ${dirModeOf(p) === 'suggested' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600'}`}
                              >Suggested</button>
                              <button
                                type="button"
                                onClick={() => setDirMode(p.integrationProductId, 'manual')}
                                className={`px-2 py-0.5 text-[11px] ${dirModeOf(p) === 'manual' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600'}`}
                              >Manual</button>
                            </div>
                          </div>
                          {dirModeOf(p) === 'suggested' ? (
                            itemSig(p)
                              ? <p className="rounded-md bg-slate-50 px-3 py-1.5 text-[11px] italic text-slate-600">{itemSig(p)}</p>
                              : <p className="text-[11px] text-amber-600">No suggested directions for this item — switch to Manual to enter them.</p>
                          ) : (
                            <Textarea
                              rows={3}
                              autoResize
                              className="resize-y"
                              value={itemDir[p.integrationProductId]?.text ?? ''}
                              onChange={(e) => setDirText(p.integrationProductId, e.target.value)}
                              placeholder="Type the directions (sig) for this item…"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
  };

  /**
   * Saturday-delivery opt-in + special instructions — shown for every order,
   * vials or pens, since a pens order ships on the same Mon-Thu cutoff. Ridden
   * along in order metadata (see `submit`) and, informationally, in Axiom's
   * own order payload.
   *
   * Was buried inside the collapsible SHIPPING section, which starts (and
   * usually stays) collapsed, and never rendered at all for a pens order — no
   * SHIPPING category exists there. Adan: "todavia no veo lo de los sabados."
   */
  const saturdayAndInstructions = (
    <>
      <label className="flex cursor-pointer items-center gap-2.5 pt-1">
        <input
          type="checkbox"
          checked={saturdayOk}
          onChange={(e) => setSaturdayOk(e.target.checked)}
          className="h-4 w-4 shrink-0 rounded border-blue-300 text-blue-600"
        />
        <span className="text-slate-800">Saturday Delivery OK?</span>
      </label>
      <div className="pt-0.5">
        <label className="mb-1 block font-medium text-slate-600">
          Special delivery instructions (optional)
        </label>
        <textarea
          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={2}
          value={specialInstructions}
          onChange={(e) => setSpecialInstructions(e.target.value)}
          placeholder="e.g. gate code, leave at side door"
        />
      </div>
    </>
  );

  // ── Item picker pieces ─────────────────────────────────────────────────────
  // Shared by the single-integration picker and the combined cart's sections,
  // so the two can never drift into different pickers.
  const searchEl = (
    <div className="border-b border-slate-100 px-4 py-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search items by name or SKU…"
          className="pl-8"
        />
      </div>
    </div>
  );
  const pensSplitEl = (
    /* Pen split — the same peptides offered twice: with the injector pen
       (first order) or as a bare cartridge (refill). */
    <div className="divide-y divide-slate-100">
      <p className="bg-slate-50 px-4 py-2.5 text-[11px] leading-relaxed text-slate-600">
        Peptides are injected with a reusable pen. Choose{' '}
        <span className="font-medium text-slate-800">Pen + Cartridge</span> if the patient doesn’t have
        a pen yet — one ships with every cartridge
        {penBundle && penBundle.price > 0 ? <> for {usd(penBundle.price)} more each</> : null}. Choose{' '}
        <span className="font-medium text-slate-800">Cartridge Only</span> to refill a pen they already own.
      </p>
      {/* Re-order: the pen isn't repeated, so the seeded cartridges land in
          Cartridge Only. Say so rather than let staff wonder why. */}
      {penDroppedFromSeed && (
        <p className="border-t border-slate-100 bg-amber-50 px-4 py-2 text-[11px] text-amber-800">
          Re-ordered as refills — the patient already has the pen from the previous order. Move an item to
          Pen + Cartridge if they need a replacement.
        </p>
      )}
      {([
        {
          id: 'pen',
          pen: true,
          title: 'Pen + Cartridge',
          blurb: penBundle && penBundle.price > 0
            ? `Includes the reusable injector pen`
            : 'Includes the reusable injector pen',
        },
        {
          id: 'refill',
          pen: false,
          title: 'Cartridge Only — Refills',
          blurb: 'For a patient who already has a pen',
        },
      ] as const).map(({ id, pen, title, blurb }) => {
        const open = penSectionOpen[id] ?? false;
        // Selections belonging to THIS list, so a collapsed header still
        // reports what's in the cart under it.
        const picked = pensOrderable.filter(
          (p) => (pen ? penCountOf(p.integrationProductId) : refillCountOf(p.integrationProductId)) > 0,
        ).length;
        return (
          <div key={id}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setPenSectionOpen((m) => ({ ...m, [id]: !open }))}
              className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-slate-50"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-slate-700">
                  {open
                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                  {title}
                  <span className="font-normal text-slate-400">({pensOrderable.length})</span>
                </span>
                <span className="mt-0.5 block pl-5 text-[11px] font-normal text-slate-500">{blurb}</span>
              </span>
              {picked > 0 && (
                <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800">
                  {picked} selected
                </span>
              )}
            </button>
            {open && (
              <div className="divide-y divide-slate-100 px-4 pb-1">
                {pensOrderable.map((p) => renderItemRow(p, { pen }))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
  const flatEl = (
    /* Flat list — pens catalogs with no bundle row configured. */
    <div className="divide-y divide-slate-100 px-4 py-1">
      {pensOrderable.map((p) => renderItemRow(p))}
    </div>
  );
  const sectionsEl = (
    /* Collapsible category sections in the fixed contractual order. */
    <div className="divide-y divide-slate-100">
      {sections.map(({ category, products: sectionProducts }) => {
        const visible = sectionProducts.filter(matchesSearch);
        if (searchQ !== '' && visible.length === 0) return null;
        // Searching force-expands matching sections; otherwise EVERY
        // section starts collapsed so the whole catalog is scannable at
        // load (explicit toggles win). Categorized catalogs only — the
        // pens catalog has no sections and is unaffected.
        const open = searchQ !== '' ? true : (expanded[category] ?? false);
        return (
          <div key={category}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setExpanded((m) => ({ ...m, [category]: !open }))}
              className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-slate-50"
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-slate-700">
                {open
                  ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                {category}
                <span className="font-normal text-slate-400">({visible.length})</span>
              </span>
            </button>
            {open && (
              <div className="divide-y divide-slate-100 px-4 pb-1">
                {visible.map((p) => renderItemRow(p))}
              </div>
            )}
          </div>
        );
      })}
      {searchQ !== '' && sections.every((s) => s.products.filter(matchesSearch).length === 0) && (
        <p className="px-4 py-3 text-xs text-slate-500">No items match “{search}”.</p>
      )}
    </div>
  );

  // ── Combined cart (pens + vials) ───────────────────────────────────────────
  const hasQty = (p: { integrationProductId: number }) => (qty[p.integrationProductId] ?? 0) > 0;
  const sectionCounts: Record<CombinedSection, number> = {
    pens: pensOrderable.filter(hasQty).length,
    vials: vialsPickable.filter(hasQty).length,
    supplies: (penQty > 0 ? 1 : 0) + (needleQty > 0 ? 1 : 0)
      + otherNeedlePacks.filter(hasQty).length + vialsSupplies.filter(hasQty).length,
  };

  /** One stepper row of the combined Supplies section. */
  const supplyStepper = (opts: {
    product: IntegrationProduct;
    title: string;
    hint: string;
    sub: string;
    value: number;
    onChange: (n: number) => void;
  }) => {
    const until = backorderedUntil(opts.product);
    return (
      <div key={opts.product.integrationProductId} className={`flex items-center justify-between gap-3 py-2 ${until ? 'opacity-60' : ''}`}>
        <div className="min-w-0">
          <p className="flex items-center gap-1 text-sm text-slate-700">
            {opts.title}
            <Hint text={opts.hint} />
            {until && (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                {stockLabel(until)}
              </span>
            )}
          </p>
          <p className="text-[11px] text-slate-500">{opts.sub}</p>
        </div>
        <QtyStepper
          value={until ? 0 : opts.value}
          min={0}
          disabled={!!until}
          label={`${opts.title} quantity`}
          onChange={opts.onChange}
        />
      </div>
    );
  };

  const combinedSuppliesEl = (
    <div className="divide-y divide-slate-100">
      {(penAddOn || extraNeedlePack || otherNeedlePacks.length > 0) && (
        <div className="px-4 py-2">
          <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">For pens</p>
          {penAddOn && supplyStepper({
            product: penAddOn,
            title: 'Reusable injector pen',
            hint: 'A standalone pen, e.g. a replacement. A pen bought with a cartridge is cheaper — use Pen + Cartridge under Peptide Pens for that.',
            sub: `${usd(penAddOn.price)} each — standalone. To send a pen with a cartridge, pick Pen + Cartridge under Peptide Pens.`,
            value: penQty,
            onChange: setPenQty,
          })}
          {extraNeedlePack && supplyStepper({
            product: extraNeedlePack,
            title: 'Premium needle tips',
            hint: 'Premium needles already ship free with each cartridge. This is how many EXTRA packs to add (default 0).',
            sub: `${extraNeedlePack.name} — ${usd(extraNeedlePack.price)} each.`,
            value: needleQty,
            onChange: setNeedleQty,
          })}
          {otherNeedlePacks.map((p) => supplyStepper({
            product: p,
            title: p.name,
            hint: 'Additional needle-tip packs for the pen.',
            sub: `${usd(p.price)} each.`,
            value: qty[p.integrationProductId] ?? 0,
            onChange: (n) => setQty((m) => ({ ...m, [p.integrationProductId]: n })),
          }))}
        </div>
      )}
      {vialsSupplies.length > 0 && (
        <div className="px-4 py-2">
          <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">For vials</p>
          <div className="divide-y divide-slate-100">
            {vialsSupplies.map((p) => renderItemRow(p))}
          </div>
        </div>
      )}
      {!penAddOn && !extraNeedlePack && otherNeedlePacks.length === 0 && vialsSupplies.length === 0 && (
        <p className="px-4 py-3 text-xs text-slate-500">No supplies are enabled for this practice.</p>
      )}
    </div>
  );

  const combinedTabs: Array<{ id: CombinedSection; label: string }> = [
    { id: 'pens', label: 'Peptide Pens' },
    { id: 'vials', label: 'Vials' },
    { id: 'supplies', label: 'Supplies' },
  ];

  const combinedPickerEl = (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-2.5">
        <Syringe className="h-3.5 w-3.5 text-slate-400" />
        <SectionTitle required hint="One cart for pens, vials and supplies. Pens and vials are sent to Axiom as two orders; the patient pays once for both.">Items</SectionTitle>
      </div>
      <div className="border-b border-slate-100 px-4 py-2">
        <div role="tablist" aria-label="Catalog section" className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1">
          {combinedTabs.map((t) => {
            const active = combinedSection === t.id;
            const count = sectionCounts[t.id];
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setCombinedSection(t.id)}
                className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
                  active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-blue-800">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {combinedSection === 'pens' && (
        pensOrderable.length === 0 ? (
          <p className="px-4 py-3 text-xs text-amber-700">No pen items are enabled for this practice.</p>
        ) : penChoice ? pensSplitEl : flatEl
      )}
      {combinedSection === 'vials' && (
        vialsPickable.length === 0 ? (
          <p className="px-4 py-3 text-xs text-amber-700">No vial items are enabled for this practice.</p>
        ) : (
          <>
            {searchEl}
            {sectionsEl}
          </>
        )
      )}
      {combinedSection === 'supplies' && combinedSuppliesEl}
    </div>
  );

  /** Two orders, said once, where the total is. */
  const splitsInTwo = pensOrderExists && vialsOrderExists;
  const itemsSlot = (
    <div className="space-y-4">
      {/* General Axiom notices (not tied to a specific SKU) — a holiday
          changing when orders ship, an outage, etc. Item-specific notices
          (carry a sku list) render inline on their own row instead, via
          noticesFor() in renderItemRow. */}
      {generalNotices.map((n) => (
        <div
          key={n.id}
          className={
            'rounded-md border px-3 py-2 text-[11px] '
            + (n.severity === 'warning'
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-blue-50 border-blue-100 text-blue-800')
          }
        >
          {n.title && <p className="font-semibold">{n.title}</p>}
          {n.message && <p>{n.message}</p>}
        </div>
      ))}
      {/* When it actually arrives — ONLY where a shipping service is part of
          the order, which today means the vials catalog. A pens order has no
          shipping row: it pays a flat per-order fee, nobody picks a service,
          so it gets the generic policy blurb instead. */}
      {shippingProducts.length > 0 ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-[12px] space-y-1">
          <p className="font-medium text-blue-900">{slaText}</p>
          {/* The one shipping fee, read-only — never the catalog row's name. */}
          {shippingFeeShown > 0 && (
            <p className="text-blue-900" data-testid="axiom-shipping-fee">
              {SHIPPING_LABEL}: {usd(shippingFeeShown)}
            </p>
          )}
          {saturdayAndInstructions}
        </div>
      ) : (
        <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-[11px] text-blue-800 space-y-1">
          <p className="font-semibold">Shipping</p>
          <p>
            Orders ship Monday–Thursday. Order by 12 NOON ET; later
            orders start the next shipping day. Weekends and holidays don&apos;t
            count. Some products in the same order may ship separately.
          </p>
          <p className="italic">Example: order Thursday 3 PM ET → ships Monday.</p>
          {saturdayAndInstructions}
        </div>
      )}

      {/* Item picker */}
      {combined ? (
        combinedPickerEl
      ) : (
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-2.5">
          <Syringe className="h-3.5 w-3.5 text-slate-400" />
          <SectionTitle required hint="Select the peptide(s) / Rx to order and set a quantity (cartridges) for each. At least one item is required.">Items</SectionTitle>
        </div>
        {/* Search across sections — categorized (vials) catalogs only. */}
        {categorized && orderableProducts.length > 0 && searchEl}
        {orderableProducts.length === 0 ? (
          <p className="px-4 py-3 text-xs text-amber-700">
            No items are enabled for this practice. Contact your administrator.
          </p>
        ) : penChoice ? pensSplitEl : !categorized ? flatEl : sectionsEl}
      </div>
      )}

      {/* Supplies — pen + extra needles. Available with or without a peptide
          (pen/needles can be purchased on their own).
          Under the pen split the standalone pen stepper is hidden: pens come
          from the Pen + Cartridge list, and keeping a second way to add one
          would let staff pay the standalone price for a bundled pen. It still
          appears, read-only, while editing an older order that carried one —
          hiding it outright would drop a line the patient was charged for. */}
      {!combined && ((penAddOn && (!penChoice || penQty > 0)) || extraNeedlePack) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60">
          <div className="flex items-center gap-2 border-b border-amber-100 px-4 py-2.5">
            <SectionTitle hint="Supplies that ship with the order. A reusable pen is needed to administer peptides; premium needles already ship free per cartridge, and you can add an extra pack.">Supplies</SectionTitle>
            <span className="text-[11px] text-slate-500">
              {penChoice ? 'Needles included per cartridge' : 'Pen to administer; needles included per cartridge'}
            </span>
          </div>
          <div className="space-y-3 px-4 py-3">
            {penAddOn && (!penChoice || penQty > 0) && (
              <div className={`flex items-center justify-between gap-3 ${backorderedUntil(penAddOn) ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-sm text-slate-700">
                    Reusable injector pen
                    <Hint text="Peptides are administered with a reusable injector pen. Set the quantity to add (default 1); set 0 if the patient already has one." />
                    {backorderedUntil(penAddOn) && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                        {stockLabel(backorderedUntil(penAddOn)!)}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {penChoice
                      ? `${usd(penAddOn.price)} each — carried over from this order. Pens are otherwise added from the Pen + Cartridge list.`
                      : `${usd(penAddOn.price)} each — one-time purchase. Set 0 if the patient already has a pen.`}
                  </p>
                  {/* The pen was on the order being repeated but is intentionally
                      not carried over — say so, rather than let the line vanish. */}
                  {penDroppedFromSeed && (
                    <p className="text-[11px] text-amber-700">
                      Not re-ordered — the patient already has the pen from the previous order. Add a quantity if they need a replacement.
                    </p>
                  )}
                </div>
                <QtyStepper
                  value={backorderedUntil(penAddOn) ? 0 : penQty}
                  min={0}
                  disabled={!!backorderedUntil(penAddOn) || penChoice}
                  label="Pen quantity"
                  onChange={setPenQty}
                />
              </div>
            )}
            {extraNeedlePack && (
              <div className={`flex items-center justify-between gap-3 ${backorderedUntil(extraNeedlePack) ? 'opacity-60' : ''}`}>
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-sm text-slate-700">
                    Extra needle packs
                    <Hint text="Premium needles already ship free with each cartridge. This is how many EXTRA packs to add (default 0)." />
                    {backorderedUntil(extraNeedlePack) && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                        {stockLabel(backorderedUntil(extraNeedlePack)!)}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {extraNeedlePack.name} — {usd(extraNeedlePack.price)} each. Premium needles already ship free per cartridge; add extra packs here.
                  </p>
                </div>
                <QtyStepper
                  value={backorderedUntil(extraNeedlePack) ? 0 : needleQty}
                  min={0}
                  disabled={!!backorderedUntil(extraNeedlePack)}
                  label="Extra needle packs quantity"
                  onChange={setNeedleQty}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order total — items + supplies + shipping, matching what the patient is charged. */}
      {(selectedNonShipping.length > 0 || pensOrderExists) && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 space-y-1">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>Items ({selectedNonShipping.length})</span>
            <span>{usd(total)}</span>
          </div>
          {supplyTotal > 0 && (
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>Supplies</span>
              <span>{usd(supplyTotal)}</span>
            </div>
          )}
          {/* ONE line even for a mixed cart (pens fee + vials fee). */}
          {shipping > 0 && (
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>{SHIPPING_LABEL}</span>
              <span>{usd(shipping)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 pt-1.5 text-base font-semibold text-slate-900">
            <span>Total</span>
            <span>{usd(grandTotal)}</span>
          </div>
          {splitsInTwo && (
            <p className="pt-1 text-[11px] text-slate-500">
              Sent to Axiom as two orders — pens and vials. The patient pays once for both.
            </p>
          )}
        </div>
      )}

      {/* Directions are now set per item (Suggested/Manual) in the item list above. */}

      {/* Prescriber — structured fields the fulfilling pharmacy requires */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <SectionTitle hint="The prescribing provider. The fulfilling pharmacy (Pharmetika) hard-requires the structured name, credential, NPI, and phone to fill the order.">Prescriber</SectionTitle>
          <span className="text-[11px] text-slate-400">Required by the fulfilling pharmacy</span>
        </div>
        <div className="grid grid-cols-2 gap-3 px-4 py-3">
          {(prescribers?.length ?? 0) > 0 && (
            <div className="col-span-2">
              <FieldLabel hint="Pick one of this practice's physicians to autofill their name, credential, NPI, DEA and phone from their record. An NPI typed below is saved back to the selected physician for next time.">Practice physician</FieldLabel>
              <select
                value={prescriberUserId}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  const p = prescribers?.find((x) => x.userId === id);
                  if (p) applyPrescriber(p);
                  else setPrescriberUserId('');
                }}
                className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Enter manually…</option>
                {prescribers!.map((p) => (
                  <option key={p.userId} value={p.userId}>
                    {p.firstName} {p.lastName}
                    {p.credential ? `, ${p.credential}` : ''}
                    {p.npi ? ` — NPI ${p.npi}` : ' — no NPI on file'}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <FieldLabel required hint="Prescriber's legal first name (no title). Required by the pharmacy.">First name</FieldLabel>
            <Input value={physicianFirst} onChange={(e) => { setPhysicianFirst(e.target.value); setPrescriberUserId(''); }} placeholder="First name" />
          </div>
          <div>
            <FieldLabel required hint="Prescriber's legal last name. Required by the pharmacy.">Last name</FieldLabel>
            <Input value={physicianLast} onChange={(e) => { setPhysicianLast(e.target.value); setPrescriberUserId(''); }} placeholder="Last name" />
          </div>
          <div>
            <FieldLabel required hint={`Prescriber's licensure credential — ${PRESCRIBER_CREDENTIALS.join(", ")}.`}>Credential</FieldLabel>
            <select
              value={credential}
              onChange={(e) => setCredential(e.target.value)}
              className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select…</option>
              {PRESCRIBER_CREDENTIALS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <FieldLabel required hint="Prescriber's 10-digit NPI, verified live against the NPPES registry. If the prescriber has no NPI on file, the one you enter here is saved back to them on order.">NPI</FieldLabel>
            <Input value={npi} onChange={(e) => setNpi(e.target.value)} placeholder="10-digit NPI" />
            {npi !== '' && !npiValid && (
              <p className="mt-0.5 text-[11px] text-red-600">Must be at least 10 digits.</p>
            )}
            {npiValid && npiStatus === 'checking' && (
              <p className="mt-0.5 text-[11px] text-slate-500">Verifying NPI…</p>
            )}
            {npiValid && npiStatus === 'verified' && (
              <p className="mt-0.5 text-[11px] text-green-700">
                ✓ {npiResult?.name || 'Verified'}{npiResult?.state ? ` — ${npiResult?.city ? `${npiResult.city}, ` : ''}${npiResult.state}` : ''}
                {!npiOnFile ? ' · will be saved to the prescriber' : ''}
              </p>
            )}
            {npiValid && npiStatus === 'inactive' && (
              <p className="mt-0.5 text-[11px] text-amber-600">NPI found but marked inactive{npiResult?.name ? `: ${npiResult.name}` : ''}.</p>
            )}
            {npiValid && npiStatus === 'notfound' && (
              <p className="mt-0.5 text-[11px] text-red-600">NPI not found in the NPPES registry — check the number.</p>
            )}
          </div>
          <div>
            <FieldLabel required hint="Prescriber / practice phone the pharmacy can call. The order cannot be filled without it.">Phone</FieldLabel>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+15125550000" />
          </div>
          <div>
            <FieldLabel hint="DEA registration number — required only when ordering a controlled substance.">DEA (if controlled)</FieldLabel>
            <Input value={dea} onChange={(e) => setDea(e.target.value)} placeholder="BS1234563" />
          </div>
        </div>
        {/* Practice address is pulled automatically from the clinic's Axiom
            enrollment (the current account) — not entered or surfaced here. */}
      </div>
    </div>
  );

  // Confirmation summary. (supplyLines / supplyTotal / grandTotal are computed
  // above so the running total in the form can show them too.)
  // One display line per HALF of a pick, so a cartridge bought with a pen and a
  // spare of the same peptide read as what they are — a bundle and an extra —
  // instead of collapsing into one row whose price matches neither.
  const confirmLines = selectedNonShipping.flatMap((p) => {
    const id = p.integrationProductId;
    const label = brandNameOf(p) ? `${brandNameOf(p)} — ${p.name}` : p.name;
    const dirs = dirTextOf(p);
    if (!penChoice || isVialsSourced(p)) {
      const n = qty[id] ?? 1;
      return [{ key: `${id}`, label, n, amount: p.price * n, dirs }];
    }
    const pens = penCountOf(id);
    const refills = refillCountOf(id);
    const unitWithPen = p.price + (penBundle?.price ?? 0);
    return [
      ...(pens > 0
        ? [{ key: `${id}-pen`, label: `${label} + pen`, n: pens, amount: unitWithPen * pens, dirs }]
        : []),
      ...(refills > 0
        ? [{ key: `${id}-refill`, label, n: refills, amount: p.price * refills, dirs }]
        : []),
    ];
  });

  // Which orders wait for payment. Each module enforces on its own, so a split
  // cart can hold one order and send the other unpaid (#1291 review): say so
  // rather than claim the whole cart is held.
  const pensHeld = !!integration.paymentEnforced;
  const vialsHeld = !!vialsIntegration?.paymentEnforced;
  const heldNote = (() => {
    if (!(combined && splitsInTwo)) {
      const held = combined ? (vialsOrderExists ? vialsHeld : pensHeld) : pensHeld;
      return held ? 'Payment is collected next — the order is held and not sent to the pharmacy until it is paid.' : null;
    }
    if (pensHeld && vialsHeld) return 'Payment is collected next — both orders are held and not sent to the pharmacy until they are paid.';
    if (pensHeld) return 'Payment is collected next — the pens order is held until it is paid; the vials order goes to the pharmacy without waiting for payment.';
    if (vialsHeld) return 'Payment is collected next — the vials order is held until it is paid; the pens order goes to the pharmacy without waiting for payment.';
    return null;
  })();

  const confirmSlot = (
    <div className="space-y-2">
      <p className="font-semibold text-blue-900">Items:</p>
      <ul className="list-disc ml-5 space-y-0.5">
        {confirmLines.map((l) => (
          <li key={l.key} className="text-blue-800 text-sm">
            {l.label} × {l.n}
            <span className="ml-2 text-blue-600">{usd(l.amount)}</span>
            {l.dirs && <span className="block text-[11px] italic text-blue-600">Directions: {l.dirs}</span>}
          </li>
        ))}
      </ul>
      {supplyLines.length > 0 && (
        <p className="text-blue-800"><strong>Supplies added:</strong> {supplyLines.join(', ')}</p>
      )}
      {/* ONE "Shipping" line even for a mixed cart — never a product name. */}
      {shipping > 0 && (
        <p className="text-blue-800">
          <strong>{SHIPPING_LABEL}:</strong> {usd(shipping)}
        </p>
      )}
      {combined && splitsInTwo && (
        <p className="text-blue-700 italic text-xs">
          Placed as two Axiom orders (pens + vials). One payment covers both.
        </p>
      )}
      {selectedNonShipping.length > 0 && (
        // grandTotal, the figure the form shows: it includes the bundled pens,
        // which this line used to leave out.
        <p className="text-blue-900 font-semibold">Total: {usd(grandTotal)}</p>
      )}
      {(physicianFirst || physicianLast) && (
        <p className="text-blue-800">
          <strong>Prescriber:</strong> {physicianFirst} {physicianLast}{credential ? `, ${credential}` : ''}
          {npi ? ` (NPI: ${npi})` : ''}{phone ? ` · ${phone}` : ''}
        </p>
      )}
      {heldNote && <p className="text-blue-700 italic text-xs">{heldNote}</p>}
    </div>
  );

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      initialShipTo={initialShipTo}
      initialOrderSex={initialOrderSex}
      shipToChoices={shipToChoices}
      onClose={onClose}
      onOrdered={() => { /* triggered inside submit */ }}
      topSlot={topSlot}
      itemsSlot={itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
